/* eslint-disable */
import { useState, useEffect, useRef, useMemo } from "react";
import { CarFront, ScrollText, UserRound, Building2, Settings } from "lucide-react";
import {
  auth, db, functions, httpsCallable, storage, loadGoogleMaps, requestNotificationPermission, uploadFile, calcDistanceETA, genReferralCode, ORDER_CLEAR_DRIVER,
  getMatchStatusAfterConfirm, shouldPromoteScheduledPending,
  collection, addDoc, setDoc, serverTimestamp, onSnapshot, query, where, doc, updateDoc, getDoc, getDocs, orderBy, limit, Timestamp, increment,
  runTransaction, deleteField,
  RecaptchaVerifier, signInWithPhoneNumber, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail,
  fetchSignInMethodsForEmail, EmailAuthProvider, linkWithCredential, updatePassword, sendEmailVerification,
} from "./firebase";
import {
  routeInstanceDocId, formatRunDateLabel, routeScheduleSummary, getBookableDatesForRoute, todayIsoLocal,
} from "./routeHelpers";
import { useLanguage, LanguagePickerCard } from "./i18n/LanguageContext";
import { fetchCanadaGasolinePricePerLiter, estimateTripFuelCad } from "./fuelPricing";
import {
  TEAL,GOLD,GREEN,RED,WARN,BG,WHITE,TEXT,SUB,LIGHT,DC,
  Btn,Inp,Card,Bar,Wrap,Sec,Toggle,Tag,StarRating,StarDisplay,InterestPicker,InterestTags,
  AddressInput,MapPicker,GPSBtn,MapView,ChatBox,BottomNav,TabBar,LoadingOverlay,Empty,StatusBadge,PeerProfileModal,
  formatDate,formatTime,formatDateTime,
  OCCUPATION_PRESETS,EXPERTISE_PRESETS,HOBBY_PRESETS,
} from "./components";

const GENDER_OPTIONS=[
  {value:"male",label:"男",emoji:"👨"},
  {value:"female",label:"女",emoji:"👩"},
  {value:"other",label:"其他",emoji:"🧑"},
];
const genderLabel=g=>GENDER_OPTIONS.find(o=>o.value===g)?.label||"";

/** 乘客端固定路線卡：整張淺藍底，同白底列表區分 */
const FIXED_ROUTE_PASSENGER_CARD_STYLE={
  background:"#DBEAFE",
  border:`1px solid #93C5FD`,
  boxShadow:"0 2px 10px rgba(37,99,235,0.1)",
};

function normalizePhoneDigits(v){
  return String(v||"").replace(/\D/g,"").slice(-10);
}
function phoneLoginEmail(phoneDigits){
  return `${normalizePhoneDigits(phoneDigits)}@phone.8go.local`;
}

function normalizeRouteSearchText(s){
  return String(s||"").toLowerCase().replace(/[#.,]/g," ").replace(/\s+/g," ").trim();
}
/** 乘客輸入 vs 車主刊登地址：雙向包含或主要片段命中 */
function routeAddrMatchesQuery(query,routeAddr){
  const q=normalizeRouteSearchText(query);
  const r=normalizeRouteSearchText(routeAddr);
  if(!q||!r)return false;
  if(r.includes(q)||q.includes(r))return true;
  const parts=q.split(/[\s,]+/).filter(p=>p.length>1);
  if(parts.length===0)return false;
  const need=parts.length===1?1:Math.ceil(parts.length*0.51);
  return parts.filter(p=>r.includes(p)).length>=need;
}
function driverRouteMatchesSearch(rt,pickQ,destQ){
  return routeAddrMatchesQuery(pickQ,rt.pickup||"")&&routeAddrMatchesQuery(destQ,rt.dest||"");
}

const CANCEL_POLICY_WINDOW_MS=14*86400000;
const CANCEL_POLICY_MAX_WARN=3;
const SUSPEND_MS=7*86400000;

async function applyPassengerCancelPolicy(uid){
  const pref=doc(db,"passengers",uid);
  const result={warned:false,warnLevel:0,suspended:false,suspendedUntil:null};
  await runTransaction(db,async t=>{
    const snap=await t.get(pref);
    const d=snap.data()||{};
    const now=Date.now();
    let timestamps=(d.cancelEventTimestamps||[]).filter(ts=>typeof ts==="number"&&now-ts<CANCEL_POLICY_WINDOW_MS*2);
    const inWindowBefore=timestamps.filter(ts=>now-ts<CANCEL_POLICY_WINDOW_MS).length;
    timestamps=[...timestamps,now].slice(-50);
    const inWindowAfter=timestamps.filter(ts=>now-ts<CANCEL_POLICY_WINDOW_MS).length;
    let fwAfter=Number(d.frequentCancelWarnings)||0;
    let susAfter=d.accountSuspendedUntil;
    if(susAfter?.toMillis&&susAfter.toMillis()<=now)susAfter=null;
    if(inWindowAfter>=3&&inWindowBefore<3){
      fwAfter+=1;
      result.warned=true;
      result.warnLevel=fwAfter;
      if(fwAfter>=CANCEL_POLICY_MAX_WARN){
        susAfter=Timestamp.fromMillis(now+SUSPEND_MS);
        result.suspended=true;
        result.suspendedUntil=susAfter;
        fwAfter=0;
      }
    }
    const patch={
      cancelEventTimestamps:timestamps,
      cancelCount:increment(1),
      frequentCancelWarnings:fwAfter,
      accountSuspendedUntil:susAfter?susAfter:deleteField(),
    };
    t.update(pref,patch);
  });
  return result;
}

const Passenger=({onBack,bottomNavLift=0,onOpenCommunity})=>{
  const { t } = useLanguage();
  const contentPadBottom=70+bottomNavLift;
  const bottomNavItems = useMemo(() => [
    { key: "ride", Icon: CarFront, label: t("nav.ride") },
    { key: "history", Icon: ScrollText, label: t("nav.history") },
    { key: "profile", Icon: UserRound, label: t("nav.profile") },
    { key: "community", Icon: Building2, label: t("nav.community") },
    { key: "settings", Icon: Settings, label: t("nav.settings") },
  ], [t]);

  // ── AUTH STATE ──────────────────────────────────────────────────────
  const [step,setStep]=useState("phone");
  const [phone,setPhone]=useState("");
  const [smsCooldown,setSmsCooldown]=useState(0);
  const [code,setCode]=useState("");
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [password2,setPassword2]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [loginMethod,setLoginMethod]=useState("email");
  const [isLogin,setIsLogin]=useState(true);
  const [confirmResult,setConfirmResult]=useState(null);
  const [phonePassword,setPhonePassword]=useState("");
  const [phonePassword2,setPhonePassword2]=useState("");
  const [smsPurpose,setSmsPurpose]=useState("register");
  const [resetPhonePassword,setResetPhonePassword]=useState("");
  const [resetPhonePassword2,setResetPhonePassword2]=useState("");

  // ── PROFILE STATE ──────────────────────────────────────────────────
  const [riderName,setRiderName]=useState("");
  const [avatarURL,setAvatarURL]=useState(null);
  const [avatarFile,setAvatarFile]=useState(null);
  const [carSidePhotoURL,setCarSidePhotoURL]=useState(null);
  const [carSidePhotoFile,setCarSidePhotoFile]=useState(null);
  const [savedAddresses,setSavedAddresses]=useState([]);
  const [sosContacts,setSosContacts]=useState([]);
  const [referralCode,setReferralCode]=useState("");
  const [cancelCount,setCancelCount]=useState(0);
  const [accountSuspendedUntil,setAccountSuspendedUntil]=useState(null);
  const [frequentCancelWarnings,setFrequentCancelWarnings]=useState(0);
  // Social profile
  const [occupation,setOccupation]=useState("");
  const [expertise,setExpertise]=useState([]);
  const [hobbies,setHobbies]=useState([]);
  const [gender,setGender]=useState("");

  // ── ORDER STATE ────────────────────────────────────────────────────
  const [pickup,setPickup]=useState("");
  const [dest,setDest]=useState("");
  const [stops,setStops]=useState([]);
  const [pax,setPax]=useState(1);
  const [luggage,setLuggage]=useState("none");
  const [hasChild,setHasChild]=useState(false);
  const [childSeats,setChildSeats]=useState({rear:0,forward:0,booster:0});
  const [hasPet,setHasPet]=useState(false);
  const [petType,setPetType]=useState("");
  const [matchMode,setMatchMode]=useState("fast");
  const [currentOrderStatus,setCurrentOrderStatus]=useState(null);
  const [isScheduled,setIsScheduled]=useState(false);
  const [scheduleDate,setScheduleDate]=useState("");
  const [scheduleTime,setScheduleTime]=useState("");
  const [gpsLoading,setGpsLoading]=useState(false);
  const [routeMetrics,setRouteMetrics]=useState(null);
  const [routeLoading,setRouteLoading]=useState(false);
  const [liveGasCadPerL,setLiveGasCadPerL]=useState(null);
  const [mapPicker,setMapPicker]=useState(null);

  // ── MATCHING STATE ─────────────────────────────────────────────────
  const [orderId,setOrderId]=useState(null);
  const [matchedDriver,setMatchedDriver]=useState(null);
  const [etaData,setEtaData]=useState(null);
  const [chooseDrivers,setChooseDrivers]=useState([]);
  const [nearbyDrivers,setNearbyDrivers]=useState([]);
  const [nearbyLoading,setNearbyLoading]=useState(false);
  const [driverLoc,setDriverLoc]=useState(null);
  const [peerModal,setPeerModal]=useState(null);

  // ── CHAT STATE ─────────────────────────────────────────────────────
  const [chatMessages,setChatMessages]=useState([]);
  const [chatUnreadCount,setChatUnreadCount]=useState(0);
  const [routeChatBooking,setRouteChatBooking]=useState(null);
  const [routeChatMessages,setRouteChatMessages]=useState([]);

  // ── REVIEW STATE ───────────────────────────────────────────────────
  const [rating,setRating]=useState(0);
  const [recSel,setRecSel]=useState(null);
  const [recDone,setRecDone]=useState(false);

  // ── HISTORY STATE ──────────────────────────────────────────────────
  const [history,setHistory]=useState([]);
  const [historyLoading,setHistoryLoading]=useState(false);
  const [myRouteBookings,setMyRouteBookings]=useState([]);
  const [riderTotalKm,setRiderTotalKm]=useState(0);

  // ── FIXED ROUTES (browse / book) ───────────────────────────────────
  const [rideFormTab,setRideFormTab]=useState("routes");
  const [publicDriverRoutes,setPublicDriverRoutes]=useState([]);
  const [browseRoute,setBrowseRoute]=useState(null);
  const [browseRunDate,setBrowseRunDate]=useState("");
  const [routeInstanceData,setRouteInstanceData]=useState(null);
  const [routeBookPax,setRouteBookPax]=useState(1);
  const [routeBookBusy,setRouteBookBusy]=useState(false);
  /** 固定路線預留：0＝未武裝，1＝已撳第一次，再撳先送出（防誤撳） */
  const [fixedRouteBookConfirmStep,setFixedRouteBookConfirmStep]=useState(0);
  const [fixedRouteQueryPickup,setFixedRouteQueryPickup]=useState("");
  const [fixedRouteQueryDest,setFixedRouteQueryDest]=useState("");
  const [fixedRouteSearchRan,setFixedRouteSearchRan]=useState(false);
  const [fixedRouteRequestBusy,setFixedRouteRequestBusy]=useState(false);
  const [lastRequestedRouteKey,setLastRequestedRouteKey]=useState("");
  const [fixedRouteRequestDate,setFixedRouteRequestDate]=useState("");
  const [fixedRouteRequestTime,setFixedRouteRequestTime]=useState("");
  const [myRouteRequests,setMyRouteRequests]=useState([]);
  const [historyRouteRequests,setHistoryRouteRequests]=useState([]);
  const [routeRequestsLoading,setRouteRequestsLoading]=useState(false);
  const [searchResultsTab,setSearchResultsTab]=useState("all");
  const [tripHistoryTab,setTripHistoryTab]=useState("active");

  const parseDepartMinutes=(timeStr)=>{
    const m=String(timeStr||"").match(/^(\d{1,2}):(\d{2})/);
    if(!m)return 23*60+59;
    const hh=Math.max(0,Math.min(23,Number(m[1])||0));
    const mm=Math.max(0,Math.min(59,Number(m[2])||0));
    return hh*60+mm;
  };
  const nextDepartTs=(rt)=>{
    const mins=parseDepartMinutes(rt.departTime);
    const dates=getBookableDatesForRoute(rt,90);
    if(!dates.length)return Number.MAX_SAFE_INTEGER;
    const now=Date.now();
    const tsList=dates
      .map(d=>new Date(`${d}T00:00:00`).getTime()+mins*60000)
      .filter(n=>!Number.isNaN(n));
    if(!tsList.length)return Number.MAX_SAFE_INTEGER;
    const upcoming=tsList.find(n=>n>=now);
    return upcoming??tsList[0];
  };

  const sortedActiveRoutes=useMemo(()=>{
    return [...publicDriverRoutes].sort((a,b)=>nextDepartTs(a)-nextDepartTs(b));
  },[publicDriverRoutes]);

  const fixedRouteSearchResults=useMemo(()=>{
    if(!fixedRouteSearchRan)return [];
    const pu=fixedRouteQueryPickup.trim();
    const de=fixedRouteQueryDest.trim();
    if(!pu||!de)return [];
    return sortedActiveRoutes.filter(rt=>driverRouteMatchesSearch(rt,pu,de));
  },[fixedRouteSearchRan,fixedRouteQueryPickup,fixedRouteQueryDest,sortedActiveRoutes]);

  const otherActiveRouteResults=useMemo(()=>{
    if(!fixedRouteSearchRan)return [];
    const ids=new Set(fixedRouteSearchResults.map(rt=>rt.id));
    return sortedActiveRoutes.filter(rt=>!ids.has(rt.id));
  },[fixedRouteSearchRan,fixedRouteSearchResults,sortedActiveRoutes]);

  const requestedRouteKey=`${fixedRouteQueryPickup.trim().toLowerCase()}__${fixedRouteQueryDest.trim().toLowerCase()}__${fixedRouteRequestDate}__${fixedRouteRequestTime}`;

  const submitRouteRequest=async()=>{
    const pickupQ=fixedRouteQueryPickup.trim();
    const destQ=fixedRouteQueryDest.trim();
    if(!pickupQ||!destQ)return;
    if(!fixedRouteRequestDate){alert("請先選擇出發日期");return;}
    if(!fixedRouteRequestTime){alert("請先選擇出發時間");return;}
    if(fixedRouteRequestBusy)return;
    if(isRiderSuspended())return;
    try{
      setFixedRouteRequestBusy(true);
      await addDoc(collection(db,"route_requests"),{
        passengerId:auth.currentUser?.uid||null,
        passengerName:riderName||"",
        passengerPhone:phone||email||"",
        pickup:pickupQ,
        dest:destQ,
        requestedDate:fixedRouteRequestDate,
        requestedTime:fixedRouteRequestTime,
        status:"open",
        source:"passenger_fixed_route_search",
        createdAt:serverTimestamp(),
      });
      setLastRequestedRouteKey(requestedRouteKey);
      alert("✅ 已送出路線請求！有合適固定路線時會再通知你。");
    }catch(err){
      alert("送出請求失敗："+(err?.message||"請稍後再試"));
    }finally{
      setFixedRouteRequestBusy(false);
    }
  };

  // ── NAV STATE ──────────────────────────────────────────────────────
  const [navTab,setNavTab]=useState("ride");

  // ── SETTINGS SUB-STEP ──────────────────────────────────────────────
  const [settingsSub,setSettingsSub]=useState(null);
  const [newSosName,setNewSosName]=useState("");
  const [newSosPhone,setNewSosPhone]=useState("");
  const [newAddrLabel,setNewAddrLabel]=useState("");
  const [newAddrValue,setNewAddrValue]=useState("");
  const [editPhone,setEditPhone]=useState("");

  const fileRef=useRef(null);
  const carPhotoRef=useRef(null);
  const recaptchaRef=useRef(null);
  const waitingChatRef=useRef(null);
  const chatSeenIdsRef=useRef(new Set());
  const chatReadyRef=useRef(false);

  // ── AUTO LOGIN ─────────────────────────────────────────────────────
  useEffect(()=>{
    let initialCheck=true;
    const unsub=auth.onAuthStateChanged(async user=>{
      if(!initialCheck)return;
      initialCheck=false;
      if(user){
        try{
          const snap=await getDoc(doc(db,"passengers",user.uid));
          if(snap.exists()){
            const data=snap.data();
            setRiderName(data.name||"");
            setEmail(user.email||"");
            setPhone(data.phone||"");
            setAvatarURL(data.avatarURL||null);
            setCarSidePhotoURL(data.carSidePhotoURL||null);
            setSavedAddresses(data.savedAddresses||[]);
            setSosContacts(data.sosContacts||[]);
            setReferralCode(data.referralCode||genReferralCode(user.uid));
            setCancelCount(data.cancelCount||0);
            setAccountSuspendedUntil(data.accountSuspendedUntil||null);
            setFrequentCancelWarnings(data.frequentCancelWarnings||0);
            setOccupation(data.occupation||"");
            setExpertise(data.expertise||[]);
            setHobbies(data.hobbies||[]);
            setGender(data.gender||"");
            setRiderTotalKm(Number(data.totalRiddenKm)||0);
            refreshRiderTotalKm(user.uid);
            await restoreActiveOrder(user.uid);
          }
        }catch(e){console.error(e);}
      }
    });
    return()=>unsub();
  },[]);

  useEffect(()=>{
    if(step!=="choose")return;
    const loadDrivers=async()=>{
      try{
        const q=query(collection(db,"drivers"),where("status","==","approved"));
        const snap=await getDocs(q);
        setChooseDrivers(snap.docs.map(d=>({id:d.id,...d.data(),
          avgRating:d.data().ratingCount>0?(d.data().totalRating/d.data().ratingCount).toFixed(1):0,
        })));
      }catch(e){console.error(e);}
    };
    loadDrivers();
  },[step]);

  useEffect(()=>{
    if(step!=="order"||!pickup.trim())return;
    let cancelled=false;
    const run=async()=>{
      setNearbyLoading(true);
      try{
        const q=query(collection(db,"drivers"),where("status","==","approved"),where("online","==",true));
        const snap=await getDocs(q);
        const rows=await Promise.all(
          snap.docs.map(async d=>{
            const data=d.data();
            const loc=data.currentLocation;
            if(!loc?.lat||!loc?.lng)return null;
            try{
              const eta=await calcDistanceETA(`${loc.lat},${loc.lng}`,pickup);
              return {
                id:d.id,
                name:data.name||"車主",
                plate:data.plate||"",
                vehicleType:data.vehicleType||"",
                kmText:eta?.kmText||"",
                kmNum:eta?.kmNum??9999,
              };
            }catch(e){return null;}
          })
        );
        if(cancelled)return;
        setNearbyDrivers(rows.filter(Boolean).sort((a,b)=>a.kmNum-b.kmNum));
      }catch(e){
        if(!cancelled)setNearbyDrivers([]);
      }
      if(!cancelled)setNearbyLoading(false);
    };
    run();
    return()=>{cancelled=true;};
  },[step,pickup]);

  const restoreActiveOrder=async(uid)=>{
    try{
      const qG=query(collection(db,"orders"),where("passengerId","==",uid),where("status","==","grabbed"),limit(1));
      const qSP=query(collection(db,"orders"),where("passengerId","==",uid),where("status","==","scheduled_pending"),limit(1));
      const qAP=query(collection(db,"orders"),where("passengerId","==",uid),where("status","==","awaiting_passenger"),limit(1));
      const qAD=query(collection(db,"orders"),where("passengerId","==",uid),where("status","==","awaiting_driver"),limit(1));
      const qW=query(collection(db,"orders"),where("passengerId","==",uid),where("status","==","waiting"),limit(1));
      const [snapG,snapSP,snapAP,snapAD,snapW]=await Promise.all([getDocs(qG),getDocs(qSP),getDocs(qAP),getDocs(qAD),getDocs(qW)]);
      const applyDoc=(docSnap,st)=>{
        const d=docSnap.data();
        setOrderId(docSnap.id);
        setPickup(d.pickup||"");
        setDest(d.dest||"");
        if(Array.isArray(d.stops))setStops(d.stops.length?d.stops:[{address:""}]);
        setCurrentOrderStatus(st);
        if(st==="grabbed"||st==="scheduled_pending"){
          setMatchedDriver(d);
          setStep("matched");
        }else{
          setMatchedDriver(st==="waiting"?null:d);
          setStep("waiting");
        }
      };
      if(!snapG.empty){applyDoc(snapG.docs[0],"grabbed");return;}
      if(!snapSP.empty){applyDoc(snapSP.docs[0],"scheduled_pending");return;}
      if(!snapAP.empty){applyDoc(snapAP.docs[0],"awaiting_passenger");return;}
      if(!snapAD.empty){applyDoc(snapAD.docs[0],"awaiting_driver");return;}
      if(!snapW.empty){applyDoc(snapW.docs[0],"waiting");return;}
      setCurrentOrderStatus(null);
      setStep("order");
    }catch(e){
      console.error(e);
      setStep("order");
    }
  };

  // ── LISTEN FOR MATCH + DRIVER LOCATION（含預約 scheduled_pending → 到點變 grabbed）────
  useEffect(()=>{
    if(!orderId)return;
    if(!["waiting","matched","choose","order"].includes(step))return;
    const unsub=onSnapshot(doc(db,"orders",orderId),(snap)=>{
      const data=snap.data();
      if(!data)return;
      setCurrentOrderStatus(data.status);
      if(shouldPromoteScheduledPending(data)){
        updateDoc(doc(db,"orders",orderId),{status:"grabbed",grabbedAt:serverTimestamp()}).catch(()=>{});
        return;
      }
      if(data.status==="cancelled"){
        setStep("order");
        setOrderId(null);
        setMatchedDriver(null);
        setCurrentOrderStatus(null);
        return;
      }
      if(step==="choose"){
        if(data.status==="awaiting_passenger"||data.status==="awaiting_driver"){
          setMatchedDriver(data);
          setStep("waiting");
        }
        if(data.status==="grabbed"||data.status==="scheduled_pending"){
          setMatchedDriver(data);
          setStep("matched");
        }
        return;
      }
      if(step==="waiting"){
        if(data.status==="awaiting_passenger"||data.status==="awaiting_driver"){
          setMatchedDriver(data);
        }else if(data.status==="waiting"){
          setMatchedDriver(null);
        }
        if(data.status==="grabbed"||data.status==="scheduled_pending"){
          setMatchedDriver(data);
          setStep("matched");
        }
      }
      if(step==="matched"){
        if(data.status==="grabbed"||data.status==="scheduled_pending"){
          setMatchedDriver(data);
        }
      }
      if(step==="order"&&(data.status==="scheduled_pending"||data.status==="grabbed")){
        setMatchedDriver(data);
      }
      if(data.driverLocation&&data.status==="grabbed"){
        setDriverLoc(data.driverLocation);
        if(data.pickup){
          calcDistanceETA(
            `${data.driverLocation.lat},${data.driverLocation.lng}`,
            data.pickup
          ).then(eta=>{if(eta)setEtaData(eta);}).catch(()=>{});
        }
      }
    });
    return()=>unsub();
  },[step,orderId]);

  // ── LISTEN FOR CHAT MESSAGES ───────────────────────────────────────
  useEffect(()=>{
    const canChatInWaiting=step==="waiting"&&orderId&&matchedDriver?.driverId&&["awaiting_passenger","awaiting_driver"].includes(currentOrderStatus);
    if((step==="matched"&&orderId)||canChatInWaiting){
      const q=query(collection(db,"chats",orderId,"messages"),orderBy("createdAt","asc"));
      const unsub=onSnapshot(q,(snap)=>{
        const rows=snap.docs.map(d=>{
          const data=d.data();
          return{
            id:d.id,
            senderId:data.senderId,
            senderName:data.senderName,
            text:data.text,
            time:data.createdAt?formatTime(data.createdAt):"",
          };
        });
        setChatMessages(rows);

        if(!chatReadyRef.current){
          rows.forEach(m=>chatSeenIdsRef.current.add(m.id));
          chatReadyRef.current=true;
          return;
        }

        const myUid=auth.currentUser?.uid;
        let incoming=0;
        rows.forEach(m=>{
          if(chatSeenIdsRef.current.has(m.id))return;
          chatSeenIdsRef.current.add(m.id);
          if(step==="waiting"&&m.senderId&&m.senderId!==myUid)incoming+=1;
        });
        if(incoming>0)setChatUnreadCount(c=>c+incoming);
      });
      return()=>unsub();
    }
  },[step,orderId,matchedDriver?.driverId,currentOrderStatus]);

  useEffect(()=>{
    if(!routeChatBooking?.id){
      setRouteChatMessages([]);
      return;
    }
    const q=query(collection(db,"route_chats",routeChatBooking.id,"messages"),orderBy("createdAt","asc"));
    const unsub=onSnapshot(q,(snap)=>{
      setRouteChatMessages(snap.docs.map(d=>{
        const data=d.data();
        return{
          id:d.id,
          senderId:data.senderId,
          senderName:data.senderName,
          text:data.text,
          time:data.createdAt?formatTime(data.createdAt):"",
        };
      }));
    });
    return()=>unsub();
  },[routeChatBooking?.id]);

  useEffect(()=>{
    chatSeenIdsRef.current=new Set();
    chatReadyRef.current=false;
    setChatUnreadCount(0);
  },[orderId]);

  useEffect(()=>{
    if(step!=="waiting")setChatUnreadCount(0);
  },[step]);

  const sendChatMessage=(text)=>{
    if(!orderId||!auth.currentUser)return;
    setChatUnreadCount(0);
    addDoc(collection(db,"chats",orderId,"messages"),{
      senderId:auth.currentUser.uid,
      senderName:riderName,
      senderRole:"passenger",
      text,
      createdAt:serverTimestamp(),
    }).catch(e=>console.error("Chat send failed:",e));
  };

  const sendRouteChatMessage=(text)=>{
    if(!routeChatBooking?.id||!auth.currentUser)return;
    addDoc(collection(db,"route_chats",routeChatBooking.id,"messages"),{
      senderId:auth.currentUser.uid,
      senderName:riderName||"乘客",
      senderRole:"passenger",
      text,
      createdAt:serverTimestamp(),
    }).catch(e=>console.error("Route chat send failed:",e));
  };

  const focusWaitingChat=()=>{
    setChatUnreadCount(0);
    waitingChatRef.current?.scrollIntoView({behavior:"smooth",block:"start"});
  };

  // ── SMS COOLDOWN ───────────────────────────────────────────────────
  useEffect(()=>{
    if(smsCooldown<=0)return;
    const t=setInterval(()=>setSmsCooldown(c=>c-1),1000);
    return()=>clearInterval(t);
  },[smsCooldown]);

  // ── GPS ─────────────────────────────────────────────────────────────
  const getGPS=()=>{
    setGpsLoading(true);
    if(!navigator.geolocation){setGpsLoading(false);return;}
    navigator.geolocation.getCurrentPosition(
      pos=>{
        const{latitude:lat,longitude:lng}=pos.coords;
        loadGoogleMaps().then(()=>{
          const gc=new window.google.maps.Geocoder();
          gc.geocode({location:{lat,lng}},(results,status)=>{
            setGpsLoading(false);
            if(status==="OK"&&results[0])setPickup(results[0].formatted_address);
            else setPickup(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          });
        });
      },
      ()=>{setGpsLoading(false);alert("無法獲取位置，請手動輸入");},
      {enableHighAccuracy:true,timeout:10000}
    );
  };

  // ── ROUTE METRICS (distance / time only — no suggested dollar fare) ─
  const computeRouteMetrics=()=>{
    if(!pickup||!dest)return;
    setRouteLoading(true);
    loadGoogleMaps().then(()=>{
      const allPoints=[pickup,...stops.map(s=>s.address).filter(Boolean),dest];
      const svc=new window.google.maps.DistanceMatrixService();
      const promises=[];
      for(let i=0;i<allPoints.length-1;i++){
        promises.push(new Promise((resolve)=>{
          svc.getDistanceMatrix({
            origins:[allPoints[i]],destinations:[allPoints[i+1]],
            travelMode:"DRIVING",unitSystem:window.google.maps.UnitSystem.METRIC,
          },(res,status)=>{
            if(status==="OK"&&res.rows[0].elements[0].status==="OK"){
              resolve({
                km:res.rows[0].elements[0].distance.value/1000,
                min:res.rows[0].elements[0].duration.value/60,
              });
            }else resolve({km:0,min:0});
          });
        }));
      }
      Promise.all(promises).then(segments=>{
        setRouteLoading(false);
        const totalKm=segments.reduce((a,s)=>a+s.km,0);
        const totalMin=segments.reduce((a,s)=>a+s.min,0);
        setRouteMetrics({km:totalKm.toFixed(1),kmNum:totalKm,min:Math.round(totalMin)});
      });
    });
  };

  useEffect(()=>{
    if(pickup&&dest)computeRouteMetrics();
  },[pickup,dest,stops.length]);

  useEffect(()=>{
    if(step!=="matched")return;
    let cancelled=false;
    fetchCanadaGasolinePricePerLiter()
      .then(r=>{if(!cancelled)setLiveGasCadPerL(r.cadPerLiter);})
      .catch(()=>{if(!cancelled)setLiveGasCadPerL(null);});
    return()=>{cancelled=true;};
  },[step]);

  // ── LOAD HISTORY ───────────────────────────────────────────────────
  const loadHistory=async()=>{
    if(!auth.currentUser)return;
    setHistoryLoading(true);
    const uid=auth.currentUser?.uid;
    if(!uid){
      setHistoryLoading(false);
      return;
    }
    const toMillis=v=>{
      if(!v)return 0;
      if(typeof v?.toMillis==="function")return v.toMillis();
      const t=new Date(v).getTime();
      return Number.isNaN(t)?0:t;
    };
    try{
      const q=query(collection(db,"orders"),where("passengerId","==",uid),orderBy("createdAt","desc"),limit(50));
      const snap=await getDocs(q);
      setHistory(snap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){
      console.error("[History] orders query failed",e);
      setHistory([]);
    }
    try{
      const qb=query(collection(db,"route_bookings"),where("passengerId","==",uid),orderBy("createdAt","desc"),limit(40));
      const bsnap=await getDocs(qb);
      setMyRouteBookings(bsnap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){
      console.error("[History] route_bookings orderBy failed, fallback",e);
      try{
        const qbFb=query(collection(db,"route_bookings"),where("passengerId","==",uid),limit(80));
        const bsnapFb=await getDocs(qbFb);
        const rows=bsnapFb.docs.map(d=>({id:d.id,...d.data()}));
        rows.sort((a,b)=>toMillis(b.createdAt)-toMillis(a.createdAt));
        setMyRouteBookings(rows.slice(0,40));
      }catch(e2){
        console.error("[History] route_bookings fallback also failed",e2);
        setMyRouteBookings([]);
      }
    }
    try{
      const qr=query(collection(db,"route_requests"),where("passengerId","==",uid),orderBy("createdAt","desc"),limit(60));
      const rsnap=await getDocs(qr);
      setHistoryRouteRequests(rsnap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){
      console.error("[History] route_requests orderBy query failed, fallback to client sort",e);
      try{
        const qrFallback=query(collection(db,"route_requests"),where("passengerId","==",uid),limit(120));
        const rsnapFallback=await getDocs(qrFallback);
        const rows=rsnapFallback.docs.map(d=>({id:d.id,...d.data()}));
        rows.sort((a,b)=>toMillis(b.createdAt)-toMillis(a.createdAt));
        setHistoryRouteRequests(rows.slice(0,60));
      }catch(e2){
        console.error("[History] route_requests fallback query failed",e2);
        setHistoryRouteRequests([]);
      }
    }
    refreshRiderTotalKm(uid);
    setHistoryLoading(false);
  };

  const refreshRiderTotalKm=async(uid)=>{
    if(!uid)return 0;
    try{
      const qKm=query(collection(db,"orders"),where("passengerId","==",uid),where("status","==","completed"),limit(300));
      const snapKm=await getDocs(qKm);
      const km=snapKm.docs.reduce((acc,d)=>{
        const n=parseFloat(String(d.data()?.distKm??"0"));
        return acc+(Number.isFinite(n)?n:0);
      },0);
      const rounded=Math.round(km*10)/10;
      setRiderTotalKm(rounded);
      await updateDoc(doc(db,"passengers",uid),{totalRiddenKm:rounded});
      return rounded;
    }catch(e){
      return 0;
    }
  };

  const isRiderSuspended=()=>{
    if(accountSuspendedUntil?.toMillis?.()>Date.now()){
      alert(`帳戶因頻繁取消已暫停使用預約／Carpool，直至 ${formatDateTime(accountSuspendedUntil)}。`);
      return true;
    }
    return false;
  };

  useEffect(()=>{
    if(step!=="order"||rideFormTab!=="routes")return;
    const q=query(collection(db,"driver_routes"),where("active","==",true));
    const unsub=onSnapshot(q,(snap)=>{
      const today=todayIsoLocal();
      const rows=snap.docs
        .map(d=>({id:d.id,...d.data()}))
        .filter(r=>{
          if(r.routeKind==="oneOff"){
            return !!(r.runDate&&r.runDate>=today);
          }
          return true;
        });
      rows.sort((a,b)=>(a.departTime||"").localeCompare(b.departTime||""));
      setPublicDriverRoutes(rows);
    },e=>console.error(e));
    return()=>unsub();
  },[step,rideFormTab]);

  useEffect(()=>{
    if(step!=="history")return;
    if(!auth.currentUser?.uid)return;
    loadHistory();
  },[step,auth.currentUser?.uid]);

  useEffect(()=>{
    if(step!=="order"||rideFormTab!=="routes"||searchResultsTab!=="requests"){
      setRouteRequestsLoading(false);
      return;
    }
    if(!auth.currentUser){
      setMyRouteRequests([]);
      return;
    }
    setRouteRequestsLoading(true);
    const q=query(
      collection(db,"route_requests"),
      where("passengerId","==",auth.currentUser.uid),
      orderBy("createdAt","desc"),
      limit(60),
    );
    const unsub=onSnapshot(q,snap=>{
      setMyRouteRequests(snap.docs.map(d=>({id:d.id,...d.data()})));
      setRouteRequestsLoading(false);
    },e=>{
      console.error(e);
      setRouteRequestsLoading(false);
    });
    return()=>unsub();
  },[step,rideFormTab,searchResultsTab]);

  useEffect(()=>{
    if(!browseRoute?.id||!browseRunDate){
      setRouteInstanceData(null);
      return;
    }
    const iid=routeInstanceDocId(browseRoute.id,browseRunDate);
    const unsub=onSnapshot(doc(db,"route_instances",iid),snap=>{
      if(snap.exists()){
        setRouteInstanceData(snap.data());
      }else{
        setRouteInstanceData({seatsBooked:0,totalSeats:browseRoute.totalSeats||4});
      }
    });
    return()=>unsub();
  },[browseRoute?.id,browseRunDate,browseRoute?.totalSeats]);

  useEffect(()=>{
    setFixedRouteBookConfirmStep(0);
  },[browseRoute?.id,browseRunDate,routeBookPax]);

  const confirmBookFixedRoute=async()=>{
    if(!browseRoute||!browseRunDate||!auth.currentUser)return;
    if(isRiderSuspended())return;
    setRouteBookBusy(true);
    try{
      await runTransaction(db,async t=>{
        const rRef=doc(db,"driver_routes",browseRoute.id);
        const rSnap=await t.get(rRef);
        const route=rSnap.data();
        if(!route?.active)throw new Error("此路線已停用");
        if(route.routeKind==="oneOff"&&route.runDate&&browseRunDate!==route.runDate)throw new Error("此單次路線只可預留指定嗰日");
        const cap=Math.min(8,Math.max(1,Number(route.totalSeats)||4));
        const instId=routeInstanceDocId(browseRoute.id,browseRunDate);
        const iRef=doc(db,"route_instances",instId);
        const iSnap=await t.get(iRef);
        const booked=iSnap.exists()?(iSnap.data().seatsBooked||0):0;
        if(booked+routeBookPax>cap)throw new Error("剩餘座位不足");
        t.set(iRef,{
          routeId:browseRoute.id,runDate:browseRunDate,driverId:route.driverId,
          seatsBooked:booked+routeBookPax,totalSeats:cap,updatedAt:serverTimestamp(),
        },{merge:true});
        const bRef=doc(collection(db,"route_bookings"));
        t.set(bRef,{
          routeId:browseRoute.id,driverId:route.driverId,runDate:browseRunDate,
          passengerId:auth.currentUser.uid,passengerName:riderName||"乘客",
          passengerPhone:phone||email||"",
          pickup:route.pickup,dest:route.dest,departTime:route.departTime||"",
          pax:routeBookPax,status:"confirmed",createdAt:serverTimestamp(),
          chargePerPassengerCad:route.chargePerPassengerCad??null,
          referenceCarpoolMidCad:route.referenceCarpoolMidCad??null,
          referenceCarpoolLowCad:route.referenceCarpoolLowCad??null,
          referenceCarpoolHighCad:route.referenceCarpoolHighCad??null,
        });
      });
      alert("✅ 已預留座位！請喺「紀錄」查看，並準時到上車點。");
      setFixedRouteBookConfirmStep(0);
      setBrowseRoute(null);
      loadHistory();
    }catch(err){
      alert(err.message||"預約失敗");
      setFixedRouteBookConfirmStep(0);
    }
    setRouteBookBusy(false);
  };

  const cancelMyRouteBooking=async b=>{
    if(b.status!=="confirmed")return;
    if(!window.confirm("確定取消此固定路線預約？頻繁取消會計入警告。"))return;
    try{
      await runTransaction(db,async t=>{
        const bRef=doc(db,"route_bookings",b.id);
        const bSnap=await t.get(bRef);
        const bd=bSnap.data();
        if(!bd||bd.status!=="confirmed")return;
        const iRef=doc(db,"route_instances",routeInstanceDocId(bd.routeId,bd.runDate));
        const iSnap=await t.get(iRef);
        const booked=iSnap.exists()?(iSnap.data().seatsBooked||0):0;
        t.update(bRef,{status:"cancelled",cancelledAt:serverTimestamp()});
        if(iSnap.exists()){
          t.update(iRef,{seatsBooked:Math.max(0,booked-bd.pax),updatedAt:serverTimestamp()});
        }
      });
      if(auth.currentUser){
        const r=await applyPassengerCancelPolicy(auth.currentUser.uid);
        const ps=await getDoc(doc(db,"passengers",auth.currentUser.uid));
        const pd=ps.data()||{};
        setCancelCount(pd.cancelCount||0);
        setAccountSuspendedUntil(pd.accountSuspendedUntil||null);
        setFrequentCancelWarnings(pd.frequentCancelWarnings||0);
        if(r.suspended)alert(`⚠️ 頻繁取消已達上限，帳戶暫停至 ${formatDateTime(r.suspendedUntil)}（7 日）。`);
        else if(r.warned)alert(`⚠️ 頻繁取消警告（第 ${r.warnLevel} 次，滿 ${CANCEL_POLICY_MAX_WARN} 次將停權 7 日）\n（14 日內取消累計達 3 次觸發一次警告）`);
      }
      loadHistory();
    }catch(e){alert("取消失敗："+e.message);}
  };

  const cancelRouteRequest=async req=>{
    if(req?.status!=="open")return;
    if(!window.confirm("確定取消此路線請求？"))return;
    try{
      await updateDoc(doc(db,"route_requests",req.id),{
        status:"cancelled",
        cancelledAt:serverTimestamp(),
        cancelledBy:"passenger",
      });
      if(step==="history")loadHistory();
    }catch(e){
      alert("取消請求失敗："+(e?.message||"請稍後再試"));
    }
  };

  const renderFixedRouteResultCard=(rt)=>(
    <Card key={rt.id} style={{cursor:"pointer",marginBottom:10,...FIXED_ROUTE_PASSENGER_CARD_STYLE}} onClick={()=>{
      setFixedRouteBookConfirmStep(0);
      setBrowseRoute(rt);
      const dates=getBookableDatesForRoute(rt,21);
      setBrowseRunDate(dates[0]||"");
      setRouteBookPax(1);
    }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
        <div style={{flex:1}}>
          <p style={{color:TEXT,fontWeight:800,fontSize:17,margin:"0 0 4px"}}>🚗 {rt.driverName||"車主"} · {rt.driverPlate||"—"}</p>
          <p style={{color:SUB,fontSize:15,margin:0}}>{routeScheduleSummary(rt)} · 約 {rt.departTime||"—"} 出發 · {rt.driverVtype||""}</p>
          <p style={{color:TEXT,fontSize:15,margin:"8px 0 0",lineHeight:1.45}}>📍 {rt.pickup}</p>
          <p style={{color:TEXT,fontSize:15,margin:"4px 0 0",lineHeight:1.45}}>🏁 {rt.dest}</p>
          {(()=>{
            const c=rt.chargePerPassengerCad;
            if(c==null||c==="")return null;
            const n=Number(c);
            if(Number.isNaN(n)||n<0)return null;
            return(
              <p style={{color:TEXT,fontSize:15,margin:"8px 0 0",lineHeight:1.45}}>
                💵 <b>${n.toFixed(2)} CAD</b>／位
                {rt.referenceCarpoolMidCad!=null&&rt.referenceCarpoolLowCad!=null&&rt.referenceCarpoolHighCad!=null&&(
                  <span style={{display:"block",fontSize:13,color:SUB,fontWeight:400,marginTop:2}}>
                    網上 GTA 共乘參考：約 ${Number(rt.referenceCarpoolLowCad).toFixed(2)}–${Number(rt.referenceCarpoolHighCad).toFixed(2)}／位
                  </span>
                )}
              </p>
            );
          })()}
          <p style={{color:TEAL,fontSize:14,margin:"8px 0 0",fontWeight:600}}>全車 {rt.totalSeats||4} 位 · 撳此去下一頁確認詳情 →</p>
          <button type="button" onClick={e=>{e.stopPropagation();if(rt.driverId)setPeerModal({role:"driver",uid:rt.driverId});}}
            style={{marginTop:8,background:"transparent",border:"none",color:TEAL,fontSize:15,fontWeight:700,cursor:"pointer",textDecoration:"underline",padding:0}}>
            👤 查看車主檔案
          </button>
        </div>
      </div>
    </Card>
  );

  // ── AVATAR ─────────────────────────────────────────────────────────
  const handleAvatar=e=>{
    const file=e.target.files[0];
    if(!file)return;
    setAvatarFile(file);
    const reader=new FileReader();
    reader.onload=ev=>setAvatarURL(ev.target.result);
    reader.readAsDataURL(file);
  };
  const handleCarSidePhoto=e=>{
    const file=e.target.files[0];
    if(!file)return;
    setCarSidePhotoFile(file);
    const reader=new FileReader();
    reader.onload=ev=>setCarSidePhotoURL(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveProfile=async()=>{
    if(!auth.currentUser)return;
    try{
      let url=avatarURL;
      let carUrl=carSidePhotoURL;
      if(avatarFile){
        url=await uploadFile(avatarFile,`passengers/${auth.currentUser.uid}/avatar`);
      }
      if(carSidePhotoFile){
        carUrl=await uploadFile(carSidePhotoFile,`passengers/${auth.currentUser.uid}/car-side`);
      }
      await updateDoc(doc(db,"passengers",auth.currentUser.uid),{
        name:riderName,avatarURL:url||null,
        carSidePhotoURL:carUrl||null,
        occupation:occupation||"",expertise:expertise||[],hobbies:hobbies||[],
        gender:gender||"",
      });
      setAvatarURL(url);
      setCarSidePhotoURL(carUrl);
      setAvatarFile(null);
      setCarSidePhotoFile(null);
      alert("✅ 已更新");
    }catch(e){alert("❌ 更新失敗："+e.message);}
  };

  const savePhone=async()=>{
    if(!auth.currentUser||!editPhone.trim())return;
    try{
      await updateDoc(doc(db,"passengers",auth.currentUser.uid),{phone:editPhone.trim()});
      setPhone(editPhone.trim());
      alert("✅ 電話已更新");
    }catch(e){alert("❌ 更新失敗");}
  };

  // ── SOS CONTACTS ───────────────────────────────────────────────────
  const saveSOS=async(list)=>{
    if(!auth.currentUser)return;
    setSosContacts(list);
    await updateDoc(doc(db,"passengers",auth.currentUser.uid),{sosContacts:list});
  };
  const addSOS=async()=>{
    if(!newSosName.trim()||!newSosPhone.trim())return;
    const updated=[...sosContacts,{name:newSosName.trim(),phone:newSosPhone.trim()}];
    await saveSOS(updated);
    setNewSosName("");setNewSosPhone("");
  };
  const removeSOS=async(idx)=>{
    await saveSOS(sosContacts.filter((_,i)=>i!==idx));
  };

  // ── SAVED ADDRESSES ────────────────────────────────────────────────
  const saveAddresses=async(list)=>{
    if(!auth.currentUser)return;
    setSavedAddresses(list);
    await updateDoc(doc(db,"passengers",auth.currentUser.uid),{savedAddresses:list});
  };
  const addAddress=async()=>{
    if(!newAddrLabel.trim()||!newAddrValue.trim())return;
    const updated=[...savedAddresses,{label:newAddrLabel.trim(),address:newAddrValue.trim()}];
    await saveAddresses(updated);
    setNewAddrLabel("");setNewAddrValue("");
  };
  const removeAddress=async(idx)=>{
    await saveAddresses(savedAddresses.filter((_,i)=>i!==idx));
  };

  // ── SUBMIT RATING ──────────────────────────────────────────────────
  const submitRating=async()=>{
    if(!orderId||!rating)return;
    try{
      const pre=await getDoc(doc(db,"orders",orderId));
      if(pre.data()?.status!=="grabbed"){
        alert("請喺行程正式開始後再評價。");
        return;
      }
      await updateDoc(doc(db,"orders",orderId),{rating,status:"completed",completedAt:serverTimestamp()});
      const orderSnap=await getDoc(doc(db,"orders",orderId));
      const driverId=orderSnap.data()?.driverId;
      if(driverId){
        await updateDoc(doc(db,"drivers",driverId),{
          totalRating:increment(rating),
          ratingCount:increment(1),
        });
      }
      setRecDone(true);
      if(auth.currentUser?.uid)refreshRiderTotalKm(auth.currentUser.uid);
    }catch(e){alert("❌ 提交失敗");}
  };

  // ── SHARE TRIP ─────────────────────────────────────────────────────
  const shareTrip=()=>{
    const msg=encodeURIComponent(
      `我係${riderName}，喺八達Go Carpool 🚗\n`+
      `車主：${matchedDriver?.driverName||"未知"}\n`+
      `車牌：${matchedDriver?.driverPlate||"未知"}\n`+
      `由 ${pickup} 去 ${dest}\n`+
      (etaData?`預計 ${etaData.durText}\n`:"")+
      `追蹤連結：8go.ca/trip/${orderId}`
    );
    window.open(`https://wa.me/?text=${msg}`,"_blank");
  };

  // ── SOS TRIGGER ────────────────────────────────────────────────────
  const triggerSOS=()=>{
    const msg=encodeURIComponent(
      `🆘 緊急！我係${riderName}，喺八達Go Carpool\n`+
      `車主：${matchedDriver?.driverName||"未知"}\n`+
      `車牌：${matchedDriver?.driverPlate||"未知"}\n`+
      `由 ${pickup} 去 ${dest}`
    );
    if(sosContacts.length>0){
      sosContacts.forEach(c=>{
        const num=c.phone.replace(/\D/g,"");
        window.open(`https://wa.me/${num.startsWith("1")?num:"1"+num}?text=${msg}`,"_blank");
      });
    }else{
      window.open(`https://wa.me/?text=${msg}`,"_blank");
    }
  };

  // ── CANCEL ORDER ───────────────────────────────────────────────────
  const cancelOrder=async()=>{
    if(!orderId)return;
    let st=currentOrderStatus;
    try{
      const os=await getDoc(doc(db,"orders",orderId));
      st=os.data()?.status||st;
    }catch(e){/* use currentOrderStatus */}
    if(st==="grabbed"){
      if(!window.confirm("車主已同你配對，確定要取消？頻繁取消會影響你嘅帳戶。"))return;
    }else if(st==="awaiting_passenger"){
      if(!window.confirm("已有車主想接你，確定取消成個Carpool請求？"))return;
    }else if(st==="awaiting_driver"){
      if(!window.confirm("已邀請車主，確定取消成個Carpool請求？"))return;
    }else if(st==="scheduled_pending"){
      if(!window.confirm("預約已確認，確定取消？頻繁取消會影響你嘅帳戶。"))return;
    }else if(!window.confirm("確定取消搵Carpool？"))return;
    try{
      await updateDoc(doc(db,"orders",orderId),{status:"cancelled",cancelledAt:serverTimestamp(),cancelledBy:"passenger"});
      if(auth.currentUser){
        const r=await applyPassengerCancelPolicy(auth.currentUser.uid);
        const ps=await getDoc(doc(db,"passengers",auth.currentUser.uid));
        const pd=ps.data()||{};
        setCancelCount(pd.cancelCount||0);
        setAccountSuspendedUntil(pd.accountSuspendedUntil||null);
        setFrequentCancelWarnings(pd.frequentCancelWarnings||0);
        if(r.suspended)alert(`⚠️ 頻繁取消已達上限，帳戶暫停至 ${formatDateTime(r.suspendedUntil)}（7 日）。`);
        else if(r.warned)alert(`⚠️ 頻繁取消警告（第 ${r.warnLevel} 次，滿 ${CANCEL_POLICY_MAX_WARN} 次將停權 7 日）\n（14 日內取消累計達 3 次觸發一次警告）`);
      }
      setOrderId(null);
      setMatchedDriver(null);
      setDriverLoc(null);
      setChatMessages([]);
      setCurrentOrderStatus(null);
      setStep("order");
    }catch(e){alert("取消失敗");}
  };

  const passengerAcceptDriverOffer=async()=>{
    if(!orderId)return;
    try{
      const ref=doc(db,"orders",orderId);
      const snap=await getDoc(ref);
      const d=snap.data();
      if(!d)return;
      const next=getMatchStatusAfterConfirm(d);
      const patch={status:next,matchConfirmedAt:serverTimestamp()};
      if(next==="grabbed")patch.grabbedAt=serverTimestamp();
      await updateDoc(ref,patch);
    }catch(e){alert("確認失敗："+e.message);}
  };

  const passengerRejectDriverOffer=async()=>{
    if(!orderId)return;
    if(!window.confirm("婉拒呢位車主，繼續等其他車主？"))return;
    try{
      await updateDoc(doc(db,"orders",orderId),{status:"waiting",...ORDER_CLEAR_DRIVER});
      setMatchedDriver(null);
      setCurrentOrderStatus("waiting");
    }catch(e){alert("操作失敗");}
  };

  const passengerChangeDriverChoice=async()=>{
    if(!orderId)return;
    if(!window.confirm("要揀另一位車主？會取消目前對車主嘅邀請。"))return;
    try{
      await updateDoc(doc(db,"orders",orderId),{status:"waiting",...ORDER_CLEAR_DRIVER});
      setMatchedDriver(null);
      setCurrentOrderStatus("waiting");
      setStep("choose");
    }catch(e){alert("操作失敗");}
  };

  const getMinDate=()=>new Date().toISOString().split("T")[0];
  const getMaxDate=()=>{const d=new Date();d.setMonth(d.getMonth()+3);return d.toISOString().split("T")[0];};
  const routeRequestStatusUi=(status)=>{
    const map={
      open:{label:"🕐 等待配對",color:WARN},
      matched:{label:"✅ 已配對",color:TEAL},
      fulfilled:{label:"🎉 已成行",color:GREEN},
      closed:{label:"📁 已結束",color:SUB},
      cancelled:{label:"❌ 已取消",color:RED},
    };
    return map[status]||{label:status||"未知",color:SUB};
  };
  const isOrderActiveStatus=s=>["waiting","awaiting_passenger","awaiting_driver","grabbed","scheduled_pending"].includes(s);
  const todayDateOnly=new Date(new Date().toDateString());
  const filteredHistoryOrders=useMemo(()=>{
    if(tripHistoryTab==="active")return history.filter(o=>isOrderActiveStatus(o.status));
    if(tripHistoryTab==="cancelled")return history.filter(o=>o.status==="cancelled");
    return history.filter(o=>!isOrderActiveStatus(o.status)&&o.status!=="cancelled");
  },[history,tripHistoryTab]);
  const filteredHistoryBookings=useMemo(()=>{
    if(tripHistoryTab==="active"){
      return myRouteBookings.filter(b=>b.status==="confirmed"&&new Date(`${b.runDate||""}T23:59:59`)>=todayDateOnly);
    }
    if(tripHistoryTab==="cancelled")return myRouteBookings.filter(b=>b.status==="cancelled");
    return myRouteBookings.filter(b=>!(b.status==="confirmed"&&new Date(`${b.runDate||""}T23:59:59`)>=todayDateOnly)&&b.status!=="cancelled");
  },[myRouteBookings,tripHistoryTab]);
  const filteredHistoryRequests=useMemo(()=>{
    if(tripHistoryTab==="active")return historyRouteRequests.filter(r=>["open","matched"].includes(r.status));
    if(tripHistoryTab==="cancelled")return historyRouteRequests.filter(r=>r.status==="cancelled");
    return historyRouteRequests.filter(r=>!["open","matched","cancelled"].includes(r.status));
  },[historyRouteRequests,tripHistoryTab]);

  const hydratePassengerSession=async(uid)=>{
    const snap=await getDoc(doc(db,"passengers",uid));
    if(snap.exists()){
      const data=snap.data();
      setRiderName(data.name||"");
      setPhone(data.phone||"");
      setAvatarURL(data.avatarURL||null);
      setCarSidePhotoURL(data.carSidePhotoURL||null);
      setSavedAddresses(data.savedAddresses||[]);
      setSosContacts(data.sosContacts||[]);
      setReferralCode(data.referralCode||genReferralCode(uid));
      setOccupation(data.occupation||"");
      setExpertise(data.expertise||[]);
      setHobbies(data.hobbies||[]);
      setGender(data.gender||"");
      setRiderTotalKm(Number(data.totalRiddenKm)||0);
      refreshRiderTotalKm(uid);
      requestNotificationPermission(uid,"passenger");
      await restoreActiveOrder(uid);
      return true;
    }
    return false;
  };

  const ensureCurrentUserPhonePassword=async(rawPhone,newPw)=>{
    const u=auth.currentUser;
    if(!u)throw new Error("未登入，請重新驗證");
    const digits=normalizePhoneDigits(rawPhone);
    if(digits.length<10)throw new Error("電話號碼格式不正確");
    if(String(newPw||"").length<6)throw new Error("密碼最少 6 位");
    const loginEmail=phoneLoginEmail(digits);
    const hasPasswordProvider=(u.providerData||[]).some(p=>p.providerId==="password");
    if(hasPasswordProvider&&String(u.email||"").toLowerCase()===loginEmail.toLowerCase()){
      await updatePassword(u,newPw);
      return loginEmail;
    }
    const methods=await fetchSignInMethodsForEmail(auth,loginEmail);
    if(methods.length>0){
      if(hasPasswordProvider){
        await updatePassword(u,newPw);
        return loginEmail;
      }
      throw new Error("此電話已綁定另一個密碼帳戶");
    }
    const cred=EmailAuthProvider.credential(loginEmail,newPw);
    await linkWithCredential(u,cred);
    return loginEmail;
  };

  const back=()=>{
    if(settingsSub){setSettingsSub(null);return;}
    const m={
      phone:onBack,sms:()=>setStep("phone"),profile_setup:()=>setStep("sms"),
      phone_reset_password:()=>setStep("sms"),
      order:onBack,
      waiting:()=>{cancelOrder();},
      choose:()=>{cancelOrder();},
      matched:()=>{
        if(currentOrderStatus==="scheduled_pending"){
          setNavTab("ride");
          setStep("order");
        }else onBack();
      },
      review:onBack,
    };
    (m[step]||onBack)();
  };

  const openReservationFromSummary=(o)=>{
    if(!o?.id)return;
    setOrderId(o.id);
    setPickup(o.pickup||"");
    setDest(o.dest||"");
    if(Array.isArray(o.stops))setStops(o.stops.length?o.stops:[{address:""}]);
    setMatchedDriver(o);
    setCurrentOrderStatus(o.status);
    setNavTab("ride");
    if(o.status==="scheduled_pending"||o.status==="grabbed")setStep("matched");
    else if(o.status==="waiting"||o.status==="awaiting_passenger"||o.status==="awaiting_driver")setStep("waiting");
  };

  const handleNavTab=(tab)=>{
    setSettingsSub(null);
    if(tab==="community"){
      if(typeof onOpenCommunity==="function")onOpenCommunity();
      return;
    }
    setNavTab(tab);
    if(tab!=="ride"){
      setBrowseRoute(null);
      setFixedRouteSearchRan(false);
    }
    if(tab==="ride")setStep("order");
    else if(tab==="history"){loadHistory();setStep("history");}
    else if(tab==="profile")setStep("my_profile");
    else if(tab==="settings")setStep("settings");
  };

  const showBottomNav=["order","history","my_profile","settings"].includes(step);
  const isPendingReservation=currentOrderStatus==="scheduled_pending"&&orderId;

  if(routeChatBooking){
    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
        <Bar title={`固定路線聊天 · ${routeChatBooking.peerName||"車主"}`} onBack={()=>setRouteChatBooking(null)}/>
        <Wrap>
          <Card style={{background:"#F0FDFA",border:`1px solid ${TEAL}33`}}>
            <p style={{color:SUB,fontSize:14,margin:0,lineHeight:1.45}}>
              📅 {routeChatBooking.runDate||"—"} · 約 {routeChatBooking.departTime||"—"} · 👥 {routeChatBooking.pax||1} 位
            </p>
            <p style={{color:TEXT,fontSize:15,margin:"8px 0 0"}}>📍 {routeChatBooking.pickup||"—"}</p>
            <p style={{color:TEXT,fontSize:15,margin:"4px 0 0"}}>🏁 {routeChatBooking.dest||"—"}</p>
          </Card>
          <ChatBox
            messages={routeChatMessages}
            onSend={sendRouteChatMessage}
            myId={auth.currentUser?.uid}
            myName={riderName}
            h={340}
          />
        </Wrap>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: LOGIN ──────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="phone")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title={isLogin?t("bar.loginPassenger"):t("bar.registerPassenger")} onBack={back}/>
      <Wrap>
        <div id="recaptcha-container"></div>
        <Card style={{background:"#EFF6FF",border:`1px solid ${TEAL}33`,marginBottom:12}}>
          <p style={{color:SUB,fontSize:15,lineHeight:1.55,margin:0}}>
            <b style={{color:TEXT}}>統一帳戶：</b>請喺呢度<b>註冊／登入</b>。要做車主請返首頁揀「我係車主」，用<b>同一帳戶</b>登入並提交審核（正式上線後需上載文件）。
          </p>
        </Card>
        <Sec title="選擇方式">
          <div style={{display:"flex",gap:8,marginBottom:4}}>
            {[["email","📧 電郵"],["phone","📱 電話"]].map(([v,l])=>(
              <div key={v} onClick={()=>setLoginMethod(v)}
                style={{flex:1,textAlign:"center",padding:"12px",borderRadius:12,cursor:"pointer",
                  background:loginMethod===v?TEAL+"22":WHITE,
                  border:`1.5px solid ${loginMethod===v?TEAL:LIGHT}`,
                  color:loginMethod===v?TEAL:SUB,fontWeight:600,fontSize:17}}>
                {l}
              </div>
            ))}
          </div>
        </Sec>

        {loginMethod==="email"?(
          <Sec title={isLogin?"電郵登入":"電郵註冊"}>
            <Card>
              <Inp placeholder="電郵地址" value={email} onChange={e=>setEmail(e.target.value)} type="email"/>
              <div style={{position:"relative"}}>
                <Inp placeholder="密碼（最少6位）" value={password} onChange={e=>setPassword(e.target.value)} 
                  type={showPw?"text":"password"} style={{paddingRight:44}}/>
                <span onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:12,top:12,
                  cursor:"pointer",fontSize:18,userSelect:"none"}}>{showPw?"🙈":"👁️"}</span>
              </div>
              {isLogin&&(
                <p style={{textAlign:"right",margin:"4px 0 0",color:TEAL,fontSize:15,cursor:"pointer",fontWeight:600}}
                  onClick={async()=>{
                    const emailTrimmed=(email||"").trim().toLowerCase();
                    if(!emailTrimmed){alert("請先輸入電郵地址");return;}
                    try{
                      const resetResults=await Promise.allSettled([
                        sendPasswordResetEmail(auth,emailTrimmed),
                        (async()=>{
                          const fn=httpsCallable(functions,"sendCustomPasswordResetEmailV1");
                          await fn({email:emailTrimmed});
                        })(),
                      ]);
                      const resetSent=resetResults.some(r=>r.status==="fulfilled");
                      if(!resetSent){throw new Error("reset-email-all-channels-failed");}
                      alert("✅ 已發送重設密碼電郵，請檢查 Inbox / Spam。");
                    }catch(err){
                      if(err.code==="auth/user-not-found")alert("❌ 帳戶唔存在，請先註冊");
                      else if(err.code==="auth/invalid-email")alert("❌ 電郵格式唔正確");
                      else alert("❌ 發送失敗："+err.message);
                    }
                  }}>
                  忘記密碼？重設電郵密碼
                </p>
              )}
              {!isLogin&&(
                <div style={{position:"relative"}}>
                  <Inp placeholder="再次輸入密碼" value={password2} onChange={e=>setPassword2(e.target.value)}
                    type={showPw?"text":"password"} style={{paddingRight:44}}/>
                  {password2.length>0&&password2!==password&&(
                    <p style={{color:RED,fontSize:15,marginTop:-8,marginBottom:8}}>❌ 密碼不一致</p>
                  )}
                </div>
              )}
            </Card>
            <Btn label={isLogin?"登入 →":"註冊 →"}
              color={email&&password.length>=6?TEAL:"#CBD5E1"}
              textColor={email&&password.length>=6?WHITE:SUB}
              disabled={!email||password.length<6||(!isLogin&&password!==password2)}
              onClick={async()=>{
                try{
                  if(isLogin){
                    const uc=await signInWithEmailAndPassword(auth,email,password);
                    const snap=await getDoc(doc(db,"passengers",uc.user.uid));
                    if(snap.exists()){
                      const data=snap.data();
                      setRiderName(data.name||"");
                      setPhone(data.phone||"");
                      setAvatarURL(data.avatarURL||null);
                      setCarSidePhotoURL(data.carSidePhotoURL||null);
                      setSavedAddresses(data.savedAddresses||[]);
                      setSosContacts(data.sosContacts||[]);
                      setReferralCode(data.referralCode||genReferralCode(uc.user.uid));
                      setOccupation(data.occupation||"");
                      setExpertise(data.expertise||[]);
                      setHobbies(data.hobbies||[]);
                      setGender(data.gender||"");
                      setRiderTotalKm(Number(data.totalRiddenKm)||0);
                      refreshRiderTotalKm(uc.user.uid);
                      requestNotificationPermission(uc.user.uid,"passenger");
                      await restoreActiveOrder(uc.user.uid);
                    }else{
                      setStep("profile_setup");
                    }
                  }else{
                    if(password!==password2){alert("❌ 密碼不一致");return;}
                    const normalizedEmail=(email||"").trim().toLowerCase();
                    const uc=await createUserWithEmailAndPassword(auth,normalizedEmail,password);
                    setEmail(normalizedEmail);
                    const verifyResults=await Promise.allSettled([
                      sendEmailVerification(uc.user),
                      (async()=>{
                        const fn=httpsCallable(functions,"sendCustomVerificationEmailV2");
                        await fn({email:normalizedEmail});
                      })(),
                    ]);
                    const verifySent=verifyResults.some(r=>r.status==="fulfilled");
                    if(!verifySent){
                      alert("⚠️ 驗證電郵暫時發送失敗，請稍後喺登入頁按「忘記密碼」再試，或聯絡客服。");
                    }else{
                      alert("✅ 驗證電郵已發送，請檢查 Inbox / Spam。");
                    }
                    try{
                      const emailjs = (await import('@emailjs/browser')).default;
                      await emailjs.send("service_23t0zgu","template_bgj35a9",{
                        driver_name: normalizedEmail,
                        driver_plate: "新乘客註冊",
                        driver_vtype: "乘客",
                        driver_langs: "",
                        driver_bio: "",
                        submit_time: new Date().toLocaleString("zh-HK"),
                      },"lwwmBiGa9G8vQEug1");
                    }catch(e){console.log("email send failed:",e);}
                    setStep("profile_setup");
                  }
                }catch(err){
                  if(err.code==="auth/user-not-found")alert("❌ 帳戶唔存在，請先註冊");
                  else if(err.code==="auth/wrong-password")alert("❌ 密碼錯誤");
                  else if(err.code==="auth/email-already-in-use")alert("❌ 電郵已註冊，請直接登入");
                  else alert("❌ 錯誤："+err.message);
                }
              }}/>
            <p style={{textAlign:"center",marginTop:14,color:TEAL,fontSize:16,cursor:"pointer"}}
              onClick={()=>setIsLogin(!isLogin)}>
              {isLogin?"未有帳戶？立即註冊":"已有帳戶？直接登入"}
            </p>
          </Sec>
        ):(
          <Sec title={isLogin?"電話登入":"電話註冊"}>
            <Card>
              <div style={{display:"flex",gap:8,marginBottom:10}}>
                <div style={{background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:12,
                  padding:"12px 14px",color:SUB,fontSize:17,whiteSpace:"nowrap"}}>🇨🇦 +1</div>
                <Inp placeholder="647-XXX-XXXX" value={phone} onChange={e=>setPhone(normalizePhoneDigits(e.target.value))} type="tel" maxLength={10}/>
              </div>
              {isLogin?(
                <div style={{position:"relative"}}>
                  <Inp placeholder="密碼（最少6位）" value={phonePassword} onChange={e=>setPhonePassword(e.target.value)}
                    type={showPw?"text":"password"} style={{paddingRight:44}}/>
                  <span onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:12,top:12,
                    cursor:"pointer",fontSize:18,userSelect:"none"}}>{showPw?"🙈":"👁️"}</span>
                </div>
              ):(
                <>
                  <div style={{position:"relative"}}>
                    <Inp placeholder="設定登入密碼（最少6位）" value={phonePassword} onChange={e=>setPhonePassword(e.target.value)}
                      type={showPw?"text":"password"} style={{paddingRight:44}}/>
                    <span onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:12,top:12,
                      cursor:"pointer",fontSize:18,userSelect:"none"}}>{showPw?"🙈":"👁️"}</span>
                  </div>
                  <Inp placeholder="再次輸入密碼" value={phonePassword2} onChange={e=>setPhonePassword2(e.target.value)}
                    type={showPw?"text":"password"}/>
                  {phonePassword2.length>0&&phonePassword2!==phonePassword&&(
                    <p style={{color:RED,fontSize:15,marginTop:-8,marginBottom:8}}>❌ 密碼不一致</p>
                  )}
                </>
              )}
              <div style={{background:"#F0FDF4",borderRadius:10,padding:"9px 12px",display:"flex",gap:8,alignItems:"center"}}>
                <span>🔒</span><span style={{color:GREEN,fontSize:15}}>號碼只作驗證用途，絕不外洩</span>
              </div>
            </Card>
            {isLogin?(
              <>
                <Btn label="電話 + 密碼登入"
                  color={phone.length>=10&&phonePassword.length>=6?TEAL:"#CBD5E1"}
                  textColor={phone.length>=10&&phonePassword.length>=6?WHITE:SUB}
                  disabled={phone.length<10||phonePassword.length<6}
                  onClick={async()=>{
                    try{
                      const loginEmail=phoneLoginEmail(phone);
                      const uc=await signInWithEmailAndPassword(auth,loginEmail,phonePassword);
                      const hasProfile=await hydratePassengerSession(uc.user.uid);
                      if(!hasProfile)setStep("profile_setup");
                    }catch(err){
                      if(err.code==="auth/user-not-found"||err.code==="auth/invalid-credential")alert("❌ 電話或密碼錯誤");
                      else if(err.code==="auth/wrong-password")alert("❌ 密碼錯誤");
                      else alert("❌ 登入失敗："+err.message);
                    }
                  }}/>
                <p style={{textAlign:"center",marginTop:12,color:TEAL,fontSize:15,cursor:"pointer"}}
                  onClick={async()=>{
                    try{
                      const digits=normalizePhoneDigits(phone);
                      if(digits.length<10){alert("請先輸入電話號碼");return;}
                      const verifier=new RecaptchaVerifier(auth,"recaptcha-container",{size:"invisible"});
                      const result=await signInWithPhoneNumber(auth,`+1${digits}`,verifier);
                      setConfirmResult(result);
                      setSmsCooldown(30);
                      setSmsPurpose("forgot_password");
                      setCode("");
                      setStep("sms");
                    }catch(err){alert("❌ 發送失敗："+err.message);}
                  }}>
                  忘記密碼？用驗證碼重設
                </p>
              </>
            ):(
              <Btn label={smsCooldown>0?`重新發送 (${smsCooldown}秒)`:"發送驗證碼並註冊"}
                color={phone.length>=10&&smsCooldown===0&&phonePassword.length>=6&&phonePassword===phonePassword2?TEAL:"#CBD5E1"}
                textColor={phone.length>=10&&smsCooldown===0&&phonePassword.length>=6&&phonePassword===phonePassword2?WHITE:SUB}
                disabled={phone.length<10||smsCooldown>0||phonePassword.length<6||phonePassword!==phonePassword2}
                onClick={async()=>{
                  try{
                    const digits=normalizePhoneDigits(phone);
                    if(phonePassword!==phonePassword2){alert("❌ 密碼不一致");return;}
                    const verifier=new RecaptchaVerifier(auth,"recaptcha-container",{size:"invisible"});
                    const result=await signInWithPhoneNumber(auth,`+1${digits}`,verifier);
                    setConfirmResult(result);
                    setSmsCooldown(30);
                    setSmsPurpose("register");
                    setCode("");
                    setStep("sms");
                  }catch(err){alert("❌ 發送失敗："+err.message);}
                }}/>
            )}
            <p style={{textAlign:"center",marginTop:14,color:TEAL,fontSize:16,cursor:"pointer"}}
              onClick={()=>{
                setIsLogin(!isLogin);
                setPhonePassword("");
                setPhonePassword2("");
              }}>
              {isLogin?"未有帳戶？立即註冊":"已有帳戶？直接登入"}
            </p>
          </Sec>
        )}
      </Wrap>
    </div>
  );

  if(step==="sms")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title={t("bar.smsCode")} onBack={back}/>
      <Wrap>
        <p style={{color:SUB,fontSize:17,marginBottom:4}}>已發送至 +1 {phone}</p>
        <p style={{color:SUB,fontSize:15,marginBottom:16}}>📩 請檢查你嘅短訊，驗證碼5分鐘內有效</p>
        <Card>
          <Inp placeholder="輸入6位驗證碼" value={code} onChange={e=>setCode(e.target.value)} type="number" maxLength={6}/>
        </Card>
        <Btn label={smsPurpose==="forgot_password"?"確認並重設密碼":"確認註冊"}
          color={code.length>=6?TEAL:"#CBD5E1"} disabled={code.length<6}
          onClick={async()=>{
            if(!confirmResult)return;
            try{
              const uc=await confirmResult.confirm(code);
              if(smsPurpose==="forgot_password"){
                setResetPhonePassword("");
                setResetPhonePassword2("");
                setStep("phone_reset_password");
              }else{
                await ensureCurrentUserPhonePassword(phone,phonePassword);
                const hasProfile=await hydratePassengerSession(uc.user.uid);
                if(!hasProfile)setStep("profile_setup");
              }
            }catch(err){alert("❌ 驗證失敗："+(err?.message||"請重試"));}
          }}/>
      </Wrap>
    </div>
  );

  if(step==="phone_reset_password")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title="重設電話登入密碼" onBack={back}/>
      <Wrap>
        <Card style={{background:"#EFF6FF",border:`1px solid ${TEAL}33`,marginBottom:12}}>
          <p style={{color:SUB,fontSize:15,lineHeight:1.55,margin:0}}>
            已完成電話驗證，請設定新密碼。之後可以直接用「電話 + 密碼」登入，唔使再收 OTP。
          </p>
        </Card>
        <Card>
          <div style={{position:"relative"}}>
            <Inp placeholder="新密碼（最少6位）" value={resetPhonePassword} onChange={e=>setResetPhonePassword(e.target.value)}
              type={showPw?"text":"password"} style={{paddingRight:44}}/>
            <span onClick={()=>setShowPw(!showPw)} style={{position:"absolute",right:12,top:12,
              cursor:"pointer",fontSize:18,userSelect:"none"}}>{showPw?"🙈":"👁️"}</span>
          </div>
          <Inp placeholder="再次輸入新密碼" value={resetPhonePassword2} onChange={e=>setResetPhonePassword2(e.target.value)}
            type={showPw?"text":"password"}/>
          {resetPhonePassword2.length>0&&resetPhonePassword2!==resetPhonePassword&&(
            <p style={{color:RED,fontSize:15,marginTop:-8,marginBottom:8}}>❌ 密碼不一致</p>
          )}
        </Card>
        <Btn label="確認重設密碼"
          color={resetPhonePassword.length>=6&&resetPhonePassword===resetPhonePassword2?TEAL:"#CBD5E1"}
          disabled={resetPhonePassword.length<6||resetPhonePassword!==resetPhonePassword2}
          onClick={async()=>{
            try{
              if(resetPhonePassword!==resetPhonePassword2){alert("❌ 密碼不一致");return;}
              await ensureCurrentUserPhonePassword(phone,resetPhonePassword);
              const uid=auth.currentUser?.uid;
              if(uid){
                const hasProfile=await hydratePassengerSession(uid);
                if(!hasProfile){setStep("profile_setup");return;}
              }
              alert("✅ 密碼已重設，之後可用電話 + 密碼登入");
            }catch(err){
              alert("❌ 重設失敗："+(err?.message||"請稍後再試"));
            }
          }}/>
      </Wrap>
    </div>
  );

  // ── PROFILE SETUP (first time) ─────────────────────────────────────
  if(step==="profile_setup")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
      <Bar title={t("bar.profileSetup")} onBack={back}/>
      <Wrap>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div onClick={()=>fileRef.current?.click()} style={{width:80,height:80,borderRadius:"50%",background:DC,
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:40,margin:"0 auto 12px",overflow:"hidden",cursor:"pointer",border:`2px solid ${LIGHT}`}}>
            {avatarURL?<img src={avatarURL} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="avatar"/>:"👤"}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatar} style={{display:"none"}}/>
          <p style={{color:TEAL,fontSize:15,cursor:"pointer"}} onClick={()=>fileRef.current?.click()}>上載頭像 📸</p>
        </div>
        <Sec title="車側相（選填，不可見車牌）">
          <Card>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div onClick={()=>carPhotoRef.current?.click()} style={{width:120,height:72,borderRadius:10,
                background:DC,border:`1px dashed ${LIGHT}`,overflow:"hidden",display:"flex",
                alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
                {carSidePhotoURL?<img src={carSidePhotoURL} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="car-side"/>:<span style={{color:SUB,fontSize:13}}>上載側面車相</span>}
              </div>
              <div style={{flex:1}}>
                <p style={{color:SUB,fontSize:14,lineHeight:1.45,margin:0}}>請影車身側面，避免車牌入鏡；此相片只作個人檔案展示。</p>
              </div>
            </div>
            <input ref={carPhotoRef} type="file" accept="image/*" onChange={handleCarSidePhoto} style={{display:"none"}}/>
          </Card>
        </Sec>
        <Sec title="你嘅姓名">
          <Card><Inp placeholder="例：陳小明 / Ming" value={riderName} onChange={e=>setRiderName(e.target.value)}/></Card>
        </Sec>
        <Sec title="性別（必填）">
          <Card>
            <p style={{color:SUB,fontSize:15,margin:"0 0 12px",lineHeight:1.45}}>請揀一項，方便車主稱呼你</p>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {GENDER_OPTIONS.map(({value,label,emoji})=>(
                <div key={value} onClick={()=>setGender(value)}
                  style={{
                    display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:14,cursor:"pointer",
                    border:`2px solid ${gender===value?TEAL:LIGHT}`,
                    background:gender===value?TEAL+"12":WHITE,
                    boxShadow:gender===value?`0 0 0 1px ${TEAL}40`:"none",
                  }}>
                  <span style={{fontSize:28}}>{emoji}</span>
                  <span style={{color:TEXT,fontSize:18,fontWeight:700}}>{label}</span>
                  <span style={{marginLeft:"auto",color:gender===value?TEAL:SUB,fontSize:20}}>{gender===value?"✓":""}</span>
                </div>
              ))}
            </div>
          </Card>
        </Sec>
        <Sec title="聯絡電話（必填）">
          <Card>
            <div style={{display:"flex",gap:8}}>
              <div style={{background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:12,
                padding:"12px 14px",color:SUB,fontSize:17,whiteSpace:"nowrap"}}>🇨🇦 +1</div>
              <Inp placeholder="647-XXX-XXXX" value={phone} onChange={e=>setPhone(e.target.value)} type="tel" maxLength={10}/>
            </div>
          </Card>
        </Sec>

        {/* ── SOCIAL PROFILE: Coffee Chat ─────────────────────────── */}
        <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
          <p style={{color:GOLD,fontWeight:700,fontSize:17,margin:"0 0 4px"}}>☕ Coffee Chat 社交配對</p>
          <p style={{color:SUB,fontSize:15,margin:"0 0 12px"}}>填寫你嘅職業同興趣，配對有共同話題嘅車主</p>
          <Sec title="職業（選填）">
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
              {OCCUPATION_PRESETS.slice(0,12).map(o=>(
                <div key={o} onClick={()=>setOccupation(o)} style={{
                  padding:"6px 12px",borderRadius:20,fontSize:15,cursor:"pointer",
                  background:occupation===o?TEAL+"22":WHITE,
                  border:`1.5px solid ${occupation===o?TEAL:LIGHT}`,
                  color:occupation===o?TEAL:SUB}}>{o}</div>
              ))}
            </div>
            <Inp placeholder="或自訂職業..." value={OCCUPATION_PRESETS.includes(occupation)?"":occupation}
              onChange={e=>setOccupation(e.target.value)} style={{marginBottom:0}}/>
          </Sec>
          <InterestPicker title="專長（選填）" presets={EXPERTISE_PRESETS} selected={expertise} onChange={setExpertise} max={5}/>
          <InterestPicker title="嗜好（選填）" presets={HOBBY_PRESETS} selected={hobbies} onChange={setHobbies} max={5}/>
        </Card>

        <Btn label="完成設定 →" color={riderName.trim().length>=2&&gender&&phone.length>=10?TEAL:"#CBD5E1"}
          disabled={riderName.trim().length<2||!gender||phone.length<10}
          onClick={async()=>{
            if(riderName.trim().length<2||!gender)return;
            try{
              const uid=auth.currentUser?.uid;
              if(!uid)throw new Error("No user");
              let url=null;
              let carUrl=null;
              if(avatarFile){url=await uploadFile(avatarFile,`passengers/${uid}/avatar`);}
              if(carSidePhotoFile){carUrl=await uploadFile(carSidePhotoFile,`passengers/${uid}/car-side`);}
              const code=genReferralCode(uid);
              await setDoc(doc(db,"passengers",uid),{
                uid,name:riderName,phone:phone||"",email:email||"",
                avatarURL:url,carSidePhotoURL:carUrl,savedAddresses:[],sosContacts:[],
                referralCode:code,referredBy:null,cancelCount:0,
                cancelEventTimestamps:[],frequentCancelWarnings:0,
                totalRiddenKm:0,
                occupation:occupation||"",expertise:expertise||[],hobbies:hobbies||[],
                gender,
                createdAt:serverTimestamp(),fcmToken:null,
              });
              setAvatarURL(url);
              setCarSidePhotoURL(carUrl);
              setReferralCode(code);
              requestNotificationPermission(uid,"passenger");
              setStep("order");
            }catch(err){console.error(err);alert("❌ 設定失敗："+err.message);}
          }}/>
      </Wrap>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: MY PROFILE ─────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="my_profile")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
      <Bar title={t("bar.profile")} onBack={()=>handleNavTab("ride")}/>
      <Wrap>
        <Card style={{textAlign:"center",padding:24}}>
          <div onClick={()=>fileRef.current?.click()} style={{width:80,height:80,borderRadius:"50%",background:DC,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:40,margin:"0 auto 12px",
            overflow:"hidden",cursor:"pointer",border:`2px solid ${LIGHT}`}}>
            {avatarURL?<img src={avatarURL} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="avatar"/>:"👤"}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatar} style={{display:"none"}}/>
          <p style={{color:TEXT,fontWeight:700,fontSize:18,margin:"0 0 4px"}}>{riderName}</p>
          <p style={{color:SUB,fontSize:16,margin:"0 0 2px"}}>📧 {email||"未設定"}</p>
          <p style={{color:SUB,fontSize:16,margin:"0 0 2px"}}>📱 {phone?`+1 ${phone}`:"未設定"}</p>
          <p style={{color:SUB,fontSize:16,margin:"0 0 2px"}}>📏 已乘坐公里：{(Number(riderTotalKm)||0).toFixed(1)} km</p>
          {gender&&<p style={{color:SUB,fontSize:16,margin:"6px 0 0"}}>⚧ 性別：{genderLabel(gender)}</p>}
          {accountSuspendedUntil?.toMillis?.()>Date.now()&&(
            <p style={{color:RED,fontSize:15,margin:"8px 0 0",fontWeight:700}}>⛔ 暫停至 {formatDateTime(accountSuspendedUntil)}（頻繁取消）</p>
          )}
          {frequentCancelWarnings>0&&!(accountSuspendedUntil?.toMillis?.()>Date.now())&&(
            <p style={{color:WARN,fontSize:15,margin:"6px 0 0"}}>⚠️ 頻繁取消警告 {frequentCancelWarnings}/{CANCEL_POLICY_MAX_WARN}</p>
          )}
          <p style={{color:TEAL,fontSize:15,margin:"8px 0 0",cursor:"pointer"}} onClick={()=>fileRef.current?.click()}>更換頭像 📸</p>
        </Card>

        {/* Social interests display */}
        {(occupation||expertise.length>0||hobbies.length>0)&&(
          <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
            <p style={{color:GOLD,fontWeight:700,fontSize:16,margin:"0 0 8px"}}>☕ Coffee Chat 簡介</p>
            <InterestTags occupation={occupation} expertise={expertise} hobbies={hobbies}/>
          </Card>
        )}

        <Sec title="車側相（不可見車牌）">
          <Card>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div onClick={()=>carPhotoRef.current?.click()} style={{width:140,height:84,borderRadius:10,
                background:DC,border:`1px dashed ${LIGHT}`,overflow:"hidden",display:"flex",
                alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}>
                {carSidePhotoURL?<img src={carSidePhotoURL} style={{width:"100%",height:"100%",objectFit:"cover"}} alt="car-side"/>:<span style={{color:SUB,fontSize:13}}>上載側面車相</span>}
              </div>
              <p style={{color:SUB,fontSize:14,lineHeight:1.45,margin:0,flex:1}}>請確保車牌唔入鏡。此相片可俾其他用戶睇到你嘅車款外觀。</p>
            </div>
            <input ref={carPhotoRef} type="file" accept="image/*" onChange={handleCarSidePhoto} style={{display:"none"}}/>
          </Card>
        </Sec>

        <Sec title="姓名">
          <Card><Inp placeholder="姓名" value={riderName} onChange={e=>setRiderName(e.target.value)}/></Card>
        </Sec>

        <Sec title="性別">
          <Card>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {GENDER_OPTIONS.map(({value,label,emoji})=>(
                <div key={value} onClick={()=>setGender(value)}
                  style={{
                    display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:14,cursor:"pointer",
                    border:`2px solid ${gender===value?TEAL:LIGHT}`,
                    background:gender===value?TEAL+"12":WHITE,
                  }}>
                  <span style={{fontSize:28}}>{emoji}</span>
                  <span style={{color:TEXT,fontSize:18,fontWeight:700}}>{label}</span>
                  <span style={{marginLeft:"auto",color:gender===value?TEAL:SUB,fontSize:20}}>{gender===value?"✓":""}</span>
                </div>
              ))}
            </div>
          </Card>
        </Sec>

        {/* Coffee Chat profile editing */}
        <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`}}>
          <p style={{color:GOLD,fontWeight:700,fontSize:17,margin:"0 0 8px"}}>☕ 編輯 Coffee Chat 資料</p>
          <Sec title="職業">
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
              {OCCUPATION_PRESETS.slice(0,10).map(o=>(
                <div key={o} onClick={()=>setOccupation(o)} style={{
                  padding:"5px 10px",borderRadius:20,fontSize:14,cursor:"pointer",
                  background:occupation===o?TEAL+"22":WHITE,
                  border:`1.5px solid ${occupation===o?TEAL:LIGHT}`,
                  color:occupation===o?TEAL:SUB}}>{o}</div>
              ))}
            </div>
            <Inp placeholder="或自訂職業..." value={OCCUPATION_PRESETS.includes(occupation)?"":occupation}
              onChange={e=>setOccupation(e.target.value)} style={{marginBottom:0}}/>
          </Sec>
          <InterestPicker title="專長" presets={EXPERTISE_PRESETS} selected={expertise} onChange={setExpertise} max={5}/>
          <InterestPicker title="嗜好" presets={HOBBY_PRESETS} selected={hobbies} onChange={setHobbies} max={5}/>
        </Card>

        <Sec title="更新電話號碼">
          <Card>
            <div style={{display:"flex",gap:8}}>
              <div style={{background:DC,border:`1.5px solid ${LIGHT}`,borderRadius:12,
                padding:"12px 14px",color:SUB,fontSize:17,whiteSpace:"nowrap"}}>🇨🇦 +1</div>
              <Inp placeholder={phone||"647-XXX-XXXX"} value={editPhone} onChange={e=>setEditPhone(e.target.value)} type="tel" maxLength={10}/>
            </div>
            <Btn label="更新電話" color={editPhone.length>=10?TEAL:"#CBD5E1"}
              disabled={editPhone.length<10} onClick={savePhone}
              style={{marginTop:4}}/>
          </Card>
        </Sec>

        <Btn label="儲存" onClick={saveProfile}/>
      </Wrap>
      <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: SETTINGS ───────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="settings"){
    if(settingsSub==="sos")return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.sos")} onBack={()=>setSettingsSub(null)}/>
        <Wrap>
          <Card style={{background:"#FFF7ED",border:`1px solid ${WARN}33`}}>
            <p style={{color:WARN,fontSize:16,margin:0}}>🆘 Carpool時撳SOS會自動WhatsApp通知以下聯絡人</p>
          </Card>
          {sosContacts.map((c,i)=>(
            <Card key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <p style={{color:TEXT,fontWeight:600,margin:0}}>{c.name}</p>
                <p style={{color:SUB,fontSize:16,margin:0}}>{c.phone}</p>
              </div>
              <button onClick={()=>removeSOS(i)} style={{background:RED+"15",border:`1px solid ${RED}33`,
                color:RED,borderRadius:8,padding:"6px 12px",fontSize:15,cursor:"pointer"}}>刪除</button>
            </Card>
          ))}
          <Sec title="新增聯絡人">
            <Card>
              <Inp placeholder="姓名" value={newSosName} onChange={e=>setNewSosName(e.target.value)}/>
              <Inp placeholder="電話 (例：6471234567)" value={newSosPhone} onChange={e=>setNewSosPhone(e.target.value)} type="tel"/>
            </Card>
          </Sec>
          <Btn label="➕ 新增" disabled={!newSosName.trim()||!newSosPhone.trim()} onClick={addSOS}/>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );

    if(settingsSub==="addresses")return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.addresses")} onBack={()=>setSettingsSub(null)}/>
        <Wrap>
          {savedAddresses.map((a,i)=>(
            <Card key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{flex:1,marginRight:8}}>
                <p style={{color:TEXT,fontWeight:600,margin:0}}>{a.label}</p>
                <p style={{color:SUB,fontSize:15,margin:0,wordBreak:"break-all"}}>{a.address}</p>
              </div>
              <button onClick={()=>removeAddress(i)} style={{background:RED+"15",border:`1px solid ${RED}33`,
                color:RED,borderRadius:8,padding:"6px 12px",fontSize:15,cursor:"pointer",flexShrink:0}}>刪除</button>
            </Card>
          ))}
          <Sec title="新增地址">
            <Card>
              <Inp placeholder="標籤（例：屋企、公司、學校）" value={newAddrLabel} onChange={e=>setNewAddrLabel(e.target.value)}/>
              <AddressInput placeholder="地址" value={newAddrValue} onChange={setNewAddrValue} onSelect={setNewAddrValue}/>
            </Card>
          </Sec>
          <Btn label="➕ 新增" disabled={!newAddrLabel.trim()||!newAddrValue.trim()} onClick={addAddress}/>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );

    if(settingsSub==="referral")return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.referral")} onBack={()=>setSettingsSub(null)}/>
        <Wrap>
          <Card style={{textAlign:"center",padding:24}}>
            <div style={{fontSize:48,marginBottom:12}}>🎁</div>
            <p style={{color:TEXT,fontSize:18,fontWeight:700,marginBottom:4}}>你嘅推薦碼</p>
            <div style={{background:DC,borderRadius:12,padding:"14px 20px",marginBottom:16}}>
              <p style={{color:TEAL,fontSize:28,fontWeight:900,margin:0,letterSpacing:3}}>{referralCode}</p>
            </div>
            <Btn label="📤 WhatsApp 分享" onClick={()=>{
              const msg=encodeURIComponent(`用我嘅推薦碼 ${referralCode} 加入八達Go！華人社區共乘社交平台 🚗\n👉 8go.ca`);
              window.open(`https://wa.me/?text=${msg}`,"_blank");
            }}/>
          </Card>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );

    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
        <Bar title={t("bar.settings")} onBack={()=>handleNavTab("ride")}/>
        <Wrap>
          <LanguagePickerCard/>
          {[
            {key:"sos",icon:"🆘",label:"緊急聯絡人",sub:`已設定 ${sosContacts.length} 位聯絡人`},
            {key:"addresses",icon:"📍",label:"常用地址",sub:`已儲存 ${savedAddresses.length} 個地址`},
            {key:"referral",icon:"🎁",label:"推薦朋友",sub:`推薦碼：${referralCode}`},
          ].map(item=>(
            <Card key={item.key} style={{cursor:"pointer"}} onClick={()=>setSettingsSub(item.key)}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:20}}>{item.icon}</span>
                <div style={{flex:1}}>
                  <p style={{color:TEXT,fontWeight:600,fontSize:17,margin:0}}>{item.label}</p>
                  <p style={{color:SUB,fontSize:15,margin:0}}>{item.sub}</p>
                </div>
                <span style={{color:SUB}}>→</span>
              </div>
            </Card>
          ))}
          <Btn label="登出" color={RED} onClick={()=>{auth.signOut();onBack();}}/>
        </Wrap>
        <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: HISTORY ────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="history")return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
      <Bar title={t("bar.history")} onBack={()=>handleNavTab("ride")}/>
      <Wrap>
        <TabBar tabs={[
          {key:"active",label:"進行中"},
          {key:"recent",label:"最近"},
          {key:"cancelled",label:"已取消"},
        ]} active={tripHistoryTab} onChange={setTripHistoryTab}/>
        <Sec title="固定路線預約">
          {historyLoading?<p style={{color:SUB}}>載入緊…</p>:
          filteredHistoryBookings.length===0?<p style={{color:SUB,fontSize:16}}>暫無紀錄</p>:
          filteredHistoryBookings.map(b=>(
            <Card key={b.id} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <Tag label={b.status==="confirmed"?"✅ 已預留":"已取消"} color={b.status==="confirmed"?TEAL:SUB}/>
                <span style={{color:SUB,fontSize:14}}>{b.runDate} · 約 {b.departTime||"—"}</span>
              </div>
              <p style={{color:TEXT,fontSize:15,margin:"0 0 4px"}}>🚗 車主路線 · 👥 {b.pax} 位</p>
              <p style={{color:SUB,fontSize:15,margin:0}}>📍 {b.pickup}</p>
              <p style={{color:SUB,fontSize:15,margin:"4px 0 0"}}>🏁 {b.dest}</p>
              {b.chargePerPassengerCad!=null&&b.chargePerPassengerCad!==""&&!Number.isNaN(Number(b.chargePerPassengerCad))&&(
                <p style={{color:TEAL,fontSize:15,margin:"8px 0 0",fontWeight:600}}>
                  💵 每位 {Number(b.chargePerPassengerCad).toFixed(2)} CAD · 你 {b.pax} 位 → 約 {(Number(b.chargePerPassengerCad)*b.pax).toFixed(2)} CAD
                </p>
              )}
              {b.status==="confirmed"&&new Date(b.runDate+"T23:59:59")>=new Date(new Date().toDateString())&&(
                <Btn label="取消預留" color={RED} style={{marginTop:10}} onClick={()=>cancelMyRouteBooking(b)}/>
              )}
              {b.status==="confirmed"&&(
                <Btn label="💬 同車主傾偈" color={TEAL} style={{marginTop:10}}
                  onClick={()=>setRouteChatBooking({
                    id:b.id,
                    peerName:b.driverName||"車主",
                    runDate:b.runDate||"",
                    departTime:b.departTime||"",
                    pax:b.pax||1,
                    pickup:b.pickup||"",
                    dest:b.dest||"",
                  })}/>
              )}
            </Card>
          ))}
        </Sec>
        <Sec title="路線請求紀錄">
          {historyLoading?<p style={{color:SUB}}>載入緊…</p>:
          filteredHistoryRequests.length===0?<p style={{color:SUB,fontSize:16}}>暫無路線請求紀錄</p>:
          filteredHistoryRequests.map(req=>{
            const s=routeRequestStatusUi(req.status);
            return(
              <Card key={req.id} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <p style={{color:TEXT,fontSize:16,fontWeight:700,margin:0,flex:1,lineHeight:1.45}}>
                    📍 {req.pickup||"—"} → 🏁 {req.dest||"—"}
                  </p>
                  <Tag label={s.label} color={s.color}/>
                </div>
                <p style={{color:SUB,fontSize:14,margin:"8px 0 0",lineHeight:1.45}}>
                  想出發日期：{req.requestedDate||"—"}
                </p>
                <p style={{color:SUB,fontSize:14,margin:"4px 0 0",lineHeight:1.45}}>
                  想出發時間：{req.requestedTime||"—"}
                </p>
                <p style={{color:SUB,fontSize:13,margin:"4px 0 0",lineHeight:1.45}}>
                  提交時間：{req.createdAt?formatDateTime(req.createdAt):"—"}
                </p>
                {req.status==="open"&&(
                  <Btn label="取消請求" color={RED} style={{marginTop:10}} onClick={()=>cancelRouteRequest(req)}/>
                )}
              </Card>
            );
          })}
        </Sec>
        <Sec title="Carpool訂單">
        {historyLoading?<div style={{textAlign:"center",padding:40}}><div style={{fontSize:30,animation:"go-spin 1s linear infinite"}}>🔄</div></div>:
        filteredHistoryOrders.length===0?<Empty icon="📭" msg="暫無Carpool紀錄"/>:
        filteredHistoryOrders.map(o=>(
          <Card key={o.id} style={{cursor:["scheduled_pending","grabbed","waiting","awaiting_passenger","awaiting_driver"].includes(o.status)?"pointer":"default"}}
            onClick={()=>{
              if(["scheduled_pending","grabbed","waiting","awaiting_passenger","awaiting_driver"].includes(o.status))openReservationFromSummary(o);
            }}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
              <span style={{color:TEXT,fontSize:16,fontWeight:600}}>{formatDateTime(o.createdAt)}</span>
              <StatusBadge status={o.status}/>
            </div>
            <div style={{background:DC,borderRadius:10,padding:"8px 12px",marginBottom:8}}>
              <p style={{color:SUB,fontSize:15,margin:"0 0 3px"}}>📍 {o.pickup}</p>
              {o.stops?.length>0&&o.stops.map((s,i)=>(
                <p key={i} style={{color:SUB,fontSize:15,margin:"0 0 3px"}}>📌 {s.address}</p>
              ))}
              <p style={{color:SUB,fontSize:15,margin:0}}>🏁 {o.dest}</p>
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
              {o.distKm!=null&&o.distKm!==""&&<Tag label={`📏 ${o.distKm}km`} color={SUB}/>}
              <Tag label={`👥 ${o.pax}人`} color={SUB}/>
              {o.isScheduled&&<Tag label={`📅 預約`} color="#818CF8"/>}
            </div>
            {o.driverName&&(
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{color:SUB,fontSize:15}}>🚗 {o.driverName} · {o.driverPlate}</span>
                {o.rating&&<StarDisplay rating={o.rating} size={12}/>}
              </div>
            )}
          </Card>
        ))}
        </Sec>
      </Wrap>
      <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: ORDER FORM ─────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="order"){
    if(rideFormTab==="routes"&&browseRoute){
      return(
        <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
          <Bar title="確認預留" onBack={()=>setBrowseRoute(null)}/>
          {mapPicker&&<MapPicker
            title={mapPicker==="pickup"?"選擇上車地點":mapPicker==="dest"?"選擇目的地":mapPicker==="fixedRoutePu"?"選擇你想出發嘅位置":mapPicker==="fixedRouteDe"?"選擇你想去嘅位置":"選擇中途站"}
            onClose={()=>setMapPicker(null)}
            onConfirm={addr=>{
              if(mapPicker==="pickup")setPickup(addr);
              else if(mapPicker==="dest")setDest(addr);
              else if(mapPicker==="fixedRoutePu")setFixedRouteQueryPickup(addr);
              else if(mapPicker==="fixedRouteDe")setFixedRouteQueryDest(addr);
              else if(mapPicker.startsWith("stop-")){
                const idx=parseInt(mapPicker.split("-")[1]);
                setStops(p=>{const n=[...p];n[idx]={address:addr};return n;});
              }
              setMapPicker(null);
            }}/>}
          <Wrap>
            <p style={{color:SUB,fontSize:16,margin:"0 0 12px",lineHeight:1.55}}>
              請核對路線、出發日子、收費同座位；撳頂部<strong style={{color:TEXT}}> ← </strong>可返回列表再揀其他路線。
            </p>
            <Card style={{...FIXED_ROUTE_PASSENGER_CARD_STYLE,border:`2px solid ${TEAL}`}}>
              <p style={{color:TEXT,fontWeight:800,fontSize:18,margin:"0 0 6px"}}>預留座位</p>
              <p style={{color:SUB,fontSize:15,margin:0}}>{browseRoute.driverName} · {browseRoute.driverPlate} · {routeScheduleSummary(browseRoute)} · 約 {browseRoute.departTime}</p>
              <p style={{color:TEXT,margin:"10px 0 4px",fontSize:15,lineHeight:1.45}}>📍 {browseRoute.pickup}</p>
              <p style={{color:TEXT,margin:"0 0 10px",fontSize:15,lineHeight:1.45}}>🏁 {browseRoute.dest}</p>
              {(()=>{
                const pu=String(browseRoute.pickup||"").trim();
                const de=String(browseRoute.dest||"").trim();
                if(!pu||!de)return null;
                return(
                  <div style={{marginBottom:14}}>
                    <p style={{color:SUB,fontSize:14,margin:"0 0 8px",fontWeight:600}}>🗺️ 全程駕車路線（Google Maps）</p>
                    <MapView key={`fixed-rt-${browseRoute.id}`} pickupAddr={pu} destAddr={de} h={240}/>
                  </div>
                );
              })()}
              {(()=>{
                const c=browseRoute.chargePerPassengerCad;
                if(c==null||c==="")return null;
                const n=Number(c);
                if(Number.isNaN(n)||n<0)return null;
                return(
                  <div style={{background:DC,borderRadius:12,padding:12,marginBottom:12,border:`1px solid ${LIGHT}`}}>
                    <p style={{color:TEXT,fontWeight:700,margin:0,fontSize:16}}>💵 車主每位收費：<b style={{color:TEAL}}>${n.toFixed(2)} CAD</b></p>
                    <p style={{color:SUB,fontSize:12,margin:"6px 0 0",lineHeight:1.45}}>
                      同常見加拿大順風車帖：<b>每位同價</b>；你預留幾個位就 × 幾多（唔係「越多人單價自動越平」）。
                    </p>
                    {browseRoute.referenceCarpoolMidCad!=null&&browseRoute.referenceCarpoolLowCad!=null&&browseRoute.referenceCarpoolHighCad!=null&&(
                      <p style={{color:SUB,fontSize:13,margin:"6px 0 0",lineHeight:1.45}}>
                        網上 GTA 共乘參考（非官方）：每位約 ${Number(browseRoute.referenceCarpoolLowCad).toFixed(2)}–${Number(browseRoute.referenceCarpoolHighCad).toFixed(2)} CAD，中位 ${Number(browseRoute.referenceCarpoolMidCad).toFixed(2)}。實際請同車主協商。
                      </p>
                    )}
                  </div>
                );
              })()}
              <p style={{color:SUB,fontSize:15,margin:"0 0 8px"}}>
                {browseRoute.routeKind==="oneOff"
                  ?"此路線為單次出發（只可預留下面呢一日）"
                  :"揀出發日子（未來 21 日內符合星期嘅日子）"}
              </p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                {getBookableDatesForRoute(browseRoute,21).length===0?(
                  <p style={{color:WARN,fontSize:15,margin:0}}>暫無可預約日子（可能已過期）</p>
                ):getBookableDatesForRoute(browseRoute,21).map(d=>(
                  <button key={d} type="button" onClick={()=>{setBrowseRunDate(d);}}
                    style={{padding:"8px 10px",borderRadius:10,border:`1.5px solid ${browseRunDate===d?TEAL:LIGHT}`,
                      background:browseRunDate===d?TEAL+"18":WHITE,cursor:"pointer",fontSize:14,color:TEXT}}>
                    {formatRunDateLabel(d)}
                  </button>
                ))}
              </div>
              {browseRunDate&&(()=>{
                const cap=Math.min(8,Math.max(1,Number(browseRoute.totalSeats)||4));
                const booked=routeInstanceData?.seatsBooked||0;
                const left=Math.max(0,cap-booked);
                return(
                  <>
                    <p style={{fontSize:18,fontWeight:800,color:TEAL,margin:"0 0 12px"}}>
                      剩餘座位（即時）：<b>{left}</b> / {cap}
                    </p>
                    {(()=>{
                      const c=browseRoute.chargePerPassengerCad;
                      if(c==null||c==="")return null;
                      const n=Number(c);
                      if(Number.isNaN(n)||n<0)return null;
                      const tot=n*routeBookPax;
                      return(
                        <p style={{color:TEXT,fontSize:16,margin:"0 0 12px",lineHeight:1.45}}>
                          你揀 <b>{routeBookPax}</b> 位 → 車主標價合計約 <b style={{color:TEAL}}>${tot.toFixed(2)} CAD</b>（${n.toFixed(2)} × {routeBookPax}；請自行同車主確認交收）
                        </p>
                      );
                    })()}
                    <Sec title="預留人數（座位）">
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {[1,2,3,4].map(n=>(
                          <div key={n} onClick={()=>setRouteBookPax(n)} style={{
                            width:44,height:44,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",
                            fontSize:18,cursor:n>left?"not-allowed":"pointer",opacity:n>left?0.35:1,
                            background:routeBookPax===n?TEAL:DC,color:routeBookPax===n?WHITE:SUB,
                            fontWeight:routeBookPax===n?700:400,border:`1.5px solid ${routeBookPax===n?TEAL:LIGHT}`}}>{n}</div>
                        ))}
                      </div>
                    </Sec>
                    {fixedRouteBookConfirmStep===1&&(
                      <p style={{color:WARN,fontSize:15,margin:"0 0 12px",lineHeight:1.5,fontWeight:600}}>
                        ⚠️ 防誤撳：核對無誤後撳「送出預留」，或撳「取消」返回上一步。
                      </p>
                    )}
                    {fixedRouteBookConfirmStep===0?(
                      <Btn
                        label={routeBookBusy?"提交中…":`確認預留 ${routeBookPax} 位（下一步）`}
                        color={left>=routeBookPax&&!routeBookBusy?TEAL:"#CBD5E1"}
                        disabled={routeBookBusy||left<routeBookPax}
                        onClick={()=>{
                          if(routeBookBusy||left<routeBookPax)return;
                          setFixedRouteBookConfirmStep(1);
                        }}/>
                    ):(
                      <div style={{display:"flex",gap:10}}>
                        <Btn
                          label="取消"
                          color={DC}
                          textColor={SUB}
                          disabled={routeBookBusy}
                          style={{flex:1,boxShadow:"none",border:`1.5px solid ${LIGHT}`}}
                          onClick={()=>{if(!routeBookBusy)setFixedRouteBookConfirmStep(0);}}/>
                        <Btn
                          label={routeBookBusy?"提交中…":`送出預留 ${routeBookPax} 位`}
                          color={left>=routeBookPax&&!routeBookBusy?TEAL:"#CBD5E1"}
                          disabled={routeBookBusy||left<routeBookPax}
                          style={{flex:1}}
                          onClick={()=>{
                            if(routeBookBusy||left<routeBookPax)return;
                            confirmBookFixedRoute();
                          }}/>
                      </div>
                    )}
                  </>
                );
              })()}
            </Card>
            <p style={{fontSize:15,color:SUB,marginTop:14,lineHeight:1.5,textAlign:"center"}}>
              取消固定路線預約同樣會計入頻繁取消政策（14 日內每滿 3 次取消＝1 次警告；滿 3 次警告停權 7 日）。
            </p>
          </Wrap>
          <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
        </div>
      );
    }
    return(
    <div style={{minHeight:"100vh",background:BG,paddingBottom:contentPadBottom}}>
      <Bar title={t("bar.findRide")} onBack={onBack}/>
      {mapPicker&&<MapPicker
        title={mapPicker==="pickup"?"選擇上車地點":mapPicker==="dest"?"選擇目的地":mapPicker==="fixedRoutePu"?"選擇你想出發嘅位置":mapPicker==="fixedRouteDe"?"選擇你想去嘅位置":"選擇中途站"}
        onClose={()=>setMapPicker(null)}
        onConfirm={addr=>{
          if(mapPicker==="pickup")setPickup(addr);
          else if(mapPicker==="dest")setDest(addr);
          else if(mapPicker==="fixedRoutePu")setFixedRouteQueryPickup(addr);
          else if(mapPicker==="fixedRouteDe")setFixedRouteQueryDest(addr);
          else if(mapPicker.startsWith("stop-")){
            const idx=parseInt(mapPicker.split("-")[1]);
            setStops(p=>{const n=[...p];n[idx]={address:addr};return n;});
          }
          setMapPicker(null);
        }}/>}
      <Wrap>
        <p style={{color:SUB,fontSize:16,marginBottom:12}}>你好，{riderName}！搵個順路嘅車主一齊出發 👋</p>
        {!!pickup.trim()&&(
          <Card style={{background:"#EFF6FF",border:"1px solid #BFDBFE",marginBottom:12}}>
            <p style={{color:"#1D4ED8",fontWeight:700,fontSize:16,margin:"0 0 8px"}}>🚗 附近車主距離（由近至遠）</p>
            {nearbyLoading?<p style={{color:SUB,fontSize:15,margin:0}}>計算中…</p>:
              nearbyDrivers.length===0?<p style={{color:SUB,fontSize:15,margin:0}}>暫時未搵到已在線車主（只顯示距離，不顯示實際位置）</p>:
              nearbyDrivers.slice(0,8).map((d,idx)=>(
                <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:idx===Math.min(nearbyDrivers.length,8)-1?"none":"1px solid #DBEAFE"}}>
                  <span style={{color:TEXT,fontSize:15}}>{idx+1}. {d.name} {d.vehicleType?`· ${d.vehicleType}`:""}</span>
                  <span style={{color:"#1D4ED8",fontWeight:700,fontSize:15}}>{d.kmText||"—"}</span>
                </div>
              ))
            }
          </Card>
        )}

        {isPendingReservation&&matchedDriver?.scheduledTime&&(
          <Card onClick={()=>setStep("matched")} style={{cursor:"pointer",background:"#EEF2FF",border:`1px solid #818CF844`,marginBottom:14}}>
            <p style={{color:"#4338CA",fontSize:16,fontWeight:700,margin:0}}>📅 你有已確認預約（待出發）</p>
            <p style={{color:SUB,fontSize:15,margin:"6px 0 0",lineHeight:1.45}}>
              {formatDateTime(matchedDriver.scheduledTime)} · 撳此處隨時查看車主同路線
            </p>
          </Card>
        )}

        {accountSuspendedUntil?.toMillis?.()>Date.now()&&(
          <Card style={{background:RED+"12",border:`1px solid ${RED}44`,marginBottom:12}}>
            <p style={{color:RED,fontSize:16,margin:0,fontWeight:700}}>⛔ 帳戶暫停中</p>
            <p style={{color:TEXT,fontSize:15,margin:"8px 0 0",lineHeight:1.5}}>
              直至 {formatDateTime(accountSuspendedUntil)} 止，暫時無法發起Carpool或預約固定路線（頻繁取消達標準後自動停權 7 日）。
            </p>
          </Card>
        )}
        {frequentCancelWarnings>0&&!(accountSuspendedUntil?.toMillis?.()>Date.now())&&(
          <Card style={{background:WARN+"15",border:`1px solid ${WARN}44`,marginBottom:12}}>
            <p style={{color:WARN,fontSize:15,margin:0,lineHeight:1.5}}>
              ⚠️ 頻繁取消警告：已記錄 {frequentCancelWarnings} 次（滿 {CANCEL_POLICY_MAX_WARN} 次將自動停權 7 日）。14 日內每累計滿 3 次取消計 1 次警告。
            </p>
          </Card>
        )}

        <TabBar tabs={[
          {key:"routes",icon:"📍",label:t("tab.fixedRoutes")},
        ]} active={rideFormTab} onChange={(k)=>{
          setRideFormTab(k);
          setBrowseRoute(null);
          if(k!=="routes")setFixedRouteSearchRan(false);
        }}/>

        {rideFormTab==="instant"?<>
        {/* 即時/預約切換 */}
        <Sec title="出發時間">
          <div style={{display:"flex",gap:8,marginBottom:8}}>
            {[["now","⚡","即時出發"],["schedule","📅","預約"]].map(([v,i,l])=>(
              <div key={v} onClick={()=>setIsScheduled(v==="schedule")}
                style={{flex:1,textAlign:"center",padding:"12px",borderRadius:12,cursor:"pointer",
                  background:(v==="schedule"?isScheduled:!isScheduled)?TEAL+"22":WHITE,
                  border:`1.5px solid ${(v==="schedule"?isScheduled:!isScheduled)?TEAL:LIGHT}`,
                  color:(v==="schedule"?isScheduled:!isScheduled)?TEAL:SUB,fontWeight:600,fontSize:17}}>
                {i} {l}
              </div>
            ))}
          </div>
          {isScheduled&&(
            <Card className="go-fade">
              <div style={{display:"flex",gap:8}}>
                <div style={{flex:1}}>
                  <p style={{color:SUB,fontSize:15,marginBottom:4}}>日期</p>
                  <input type="date" min={getMinDate()} max={getMaxDate()} value={scheduleDate}
                    onChange={e=>setScheduleDate(e.target.value)}
                    style={{width:"100%",padding:"10px",borderRadius:10,border:`1.5px solid ${LIGHT}`,fontSize:17,color:TEXT,outline:"none"}}/>
                </div>
                <div style={{flex:1}}>
                  <p style={{color:SUB,fontSize:15,marginBottom:4}}>時間</p>
                  <input type="time" value={scheduleTime} onChange={e=>setScheduleTime(e.target.value)}
                    style={{width:"100%",padding:"10px",borderRadius:10,border:`1.5px solid ${LIGHT}`,fontSize:17,color:TEXT,outline:"none"}}/>
                </div>
              </div>
            </Card>
          )}
        </Sec>

        {/* 上車地點 */}
        <Sec title="上車地點">
          <Card>
            {savedAddresses.length>0&&(
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                {savedAddresses.map((a,i)=>(
                  <div key={i} onClick={()=>setPickup(a.address)}
                    style={{padding:"6px 12px",borderRadius:20,fontSize:15,cursor:"pointer",
                      background:pickup===a.address?TEAL+"22":DC,
                      border:`1px solid ${pickup===a.address?TEAL:LIGHT}`,
                      color:pickup===a.address?TEAL:SUB}}>
                    {a.label}
                  </div>
                ))}
              </div>
            )}
            <GPSBtn onLocate={getGPS} loading={gpsLoading}/>
            <AddressInput placeholder="或手動輸入地址..." value={pickup} onChange={setPickup} onSelect={setPickup}/>
            <div onClick={()=>setMapPicker("pickup")}
              style={{background:"#EFF6FF",border:`1px solid #BFDBFE`,borderRadius:10,
                padding:"9px 12px",display:"flex",gap:8,alignItems:"center",cursor:"pointer",marginTop:2}}>
              <span style={{fontSize:18}}>🗺️</span>
              <span style={{color:"#3B82F6",fontSize:16,fontWeight:500}}>地圖揀位</span>
            </div>
          </Card>
        </Sec>

        {/* 多站點 */}
        {stops.map((s,i)=>(
          <Sec key={i} title={`中途站 ${i+1}`}>
            <Card>
              <div style={{display:"flex",gap:8}}>
                <div style={{flex:1}}>
                  <AddressInput placeholder="中途站地址" value={s.address} onChange={v=>{
                    setStops(p=>{const n=[...p];n[i]={address:v};return n;});
                  }} onSelect={v=>{
                    setStops(p=>{const n=[...p];n[i]={address:v};return n;});
                  }}/>
                </div>
                <button onClick={()=>setStops(p=>p.filter((_,j)=>j!==i))}
                  style={{background:RED+"15",border:`1px solid ${RED}33`,color:RED,borderRadius:10,
                    padding:"8px 12px",fontSize:17,cursor:"pointer",height:44}}>✕</button>
              </div>
            </Card>
          </Sec>
        ))}
        {stops.length<3&&(
          <div onClick={()=>setStops(p=>[...p,{address:""}])}
            style={{color:TEAL,fontSize:16,fontWeight:600,cursor:"pointer",marginBottom:12,
              display:"flex",alignItems:"center",gap:6}}>
            ➕ 新增中途站
          </div>
        )}

        {/* 目的地 */}
        <Sec title="目的地">
          <Card>
            {savedAddresses.length>0&&(
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                {savedAddresses.map((a,i)=>(
                  <div key={i} onClick={()=>setDest(a.address)}
                    style={{padding:"6px 12px",borderRadius:20,fontSize:15,cursor:"pointer",
                      background:dest===a.address?TEAL+"22":DC,
                      border:`1px solid ${dest===a.address?TEAL:LIGHT}`,
                      color:dest===a.address?TEAL:SUB}}>
                    {a.label}
                  </div>
                ))}
              </div>
            )}
            <AddressInput placeholder="例：皮爾遜機場 / 市中心 Union Station" value={dest} onChange={setDest} onSelect={setDest}/>
            <div onClick={()=>setMapPicker("dest")}
              style={{background:"#EFF6FF",border:`1px solid #BFDBFE`,borderRadius:10,
                padding:"9px 12px",display:"flex",gap:8,alignItems:"center",cursor:"pointer",marginTop:2}}>
              <span style={{fontSize:18}}>🗺️</span>
              <span style={{color:"#3B82F6",fontSize:16,fontWeight:500}}>地圖揀位</span>
            </div>
          </Card>
        </Sec>

        {/* 同行人數 */}
        <Sec title="同行人數">
          <Card>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[1,2,3,4,5,6].map(n=>(
                <div key={n} onClick={()=>setPax(n)} style={{
                  width:44,height:44,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:18,cursor:"pointer",background:pax===n?TEAL:DC,color:pax===n?WHITE:SUB,
                  fontWeight:pax===n?700:400,border:`1.5px solid ${pax===n?TEAL:LIGHT}`}}>{n}</div>
              ))}
            </div>
          </Card>
        </Sec>

        {/* 行李 */}
        <Sec title="行李">
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[["none","🎒","冇行李"],["small","💼","手提"],["medium","🧳","中型"],["large","🧳","大型"]].map(([v,i,l])=>(
              <div key={v} onClick={()=>setLuggage(v)} style={{
                padding:"8px 12px",borderRadius:20,fontSize:15,cursor:"pointer",
                background:luggage===v?TEAL:WHITE,color:luggage===v?WHITE:SUB,
                border:`1.5px solid ${luggage===v?TEAL:LIGHT}`,display:"flex",alignItems:"center",gap:5}}>
                {i} {l}
              </div>
            ))}
          </div>
        </Sec>

        {/* 特殊需要 */}
        <Sec title="特殊需要">
          <Card>
            <Toggle on={hasChild} onToggle={()=>setHasChild(!hasChild)} icon="👶" label="有細路同行" sub="需要嬰兒/兒童座椅"/>
            {hasChild&&(
              <div className="go-fade" style={{marginTop:12,paddingLeft:32}}>
                <p style={{color:SUB,fontSize:15,marginBottom:8}}>需要幾多個座椅？</p>
                {[["rear","👶","後向嬰兒座椅","0–12個月"],["forward","🧒","前向兒童座椅","1–4歲"],["booster","👦","加高座椅","4–8歲"]].map(([k,i,l,sub])=>(
                  <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    marginBottom:10,background:DC,borderRadius:10,padding:"10px 12px"}}>
                    <div>
                      <div style={{color:TEXT,fontSize:16}}>{i} {l}</div>
                      <div style={{color:SUB,fontSize:14}}>{sub}</div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <button onClick={()=>setChildSeats(p=>({...p,[k]:Math.max(0,p[k]-1)}))} style={{width:28,height:28,borderRadius:"50%",border:`1px solid ${LIGHT}`,background:WHITE,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:TEAL}}>−</button>
                      <span style={{color:TEXT,fontWeight:700,minWidth:16,textAlign:"center"}}>{childSeats[k]}</span>
                      <button onClick={()=>setChildSeats(p=>({...p,[k]:p[k]+1}))} style={{width:28,height:28,borderRadius:"50%",border:"none",background:TEAL,cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",color:WHITE}}>+</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Toggle on={hasPet} onToggle={()=>setHasPet(!hasPet)} icon="🐾" label="帶寵物" sub="請先同車主確認"/>
          </Card>
        </Sec>

        {/* 配對方式 */}
        <Sec title="配對方式">
          <Card>
            {[["fast","⚡","快速配對","系統揀最近車主，30秒配對"],["choose","👀","自己揀車主","睇晒車主資料同興趣再選擇"]].map(([v,i,l,sub])=>(
              <div key={v} onClick={()=>setMatchMode(v)} style={{display:"flex",gap:12,padding:"10px 0",cursor:"pointer",
                borderBottom:v==="fast"?`1px solid ${LIGHT}`:"none"}}>
                <div style={{width:20,height:20,borderRadius:"50%",flexShrink:0,
                  border:`2px solid ${matchMode===v?TEAL:LIGHT}`,background:matchMode===v?TEAL:"transparent",
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {matchMode===v&&<div style={{width:8,height:8,borderRadius:"50%",background:WHITE}}/>}
                </div>
                <span style={{fontSize:20}}>{i}</span>
                <div>
                  <div style={{color:TEXT,fontSize:17,fontWeight:500}}>{l}</div>
                  <div style={{color:SUB,fontSize:15}}>{sub}</div>
                </div>
              </div>
            ))}
          </Card>
        </Sec>

        {/* 路程參考（無建議車資；油費待配對後按車輛油耗估算） */}
        {pickup&&dest&&(
          <Card style={{border:`1px solid ${TEAL}33`,background:"#F0FDFA"}}>
            <p style={{color:SUB,fontSize:15,marginBottom:4}}>🛣️ 路程參考</p>
            {routeLoading
              ?<p style={{color:TEAL,fontSize:18}}>計算中...</p>
              :routeMetrics
                ?<>
                  <p style={{color:TEXT,fontSize:20,fontWeight:800,margin:0}}>📏 {routeMetrics.km} km</p>
                  <p style={{color:SUB,fontSize:14,marginTop:4}}>⏱️ 約 {routeMetrics.min} 分鐘（駕車路線參考）</p>
                </>
                :<p style={{color:SUB,fontSize:16}}>輸入地址後自動計算</p>
            }
            <p style={{color:SUB,fontSize:14,marginTop:8,lineHeight:1.45}}>本平台唔提供叫車報價。配對成功後會按車主登記嘅油耗同即時參考油價顯示汽油費估算；實際分擔請雙方協商。</p>
          </Card>
        )}

        {/* 落單按鈕 */}
        <Btn label={isScheduled?"📅 預約Carpool":matchMode==="fast"?"⚡ 快速搵Carpool":"👀 睇車主再決定"}
          color={pickup&&dest&&(!isScheduled||(scheduleDate&&scheduleTime))?TEAL:"#CBD5E1"}
          disabled={!pickup||!dest||(isScheduled&&(!scheduleDate||!scheduleTime))}
          onClick={async()=>{
            if(!pickup||!dest)return;
            if(isScheduled&&(!scheduleDate||!scheduleTime)){alert("請選擇預約時間");return;}
            if(isRiderSuspended())return;
            try{
              const scheduledTime=isScheduled?Timestamp.fromDate(new Date(scheduleDate+"T"+scheduleTime)):null;
              const orderRef=await addDoc(collection(db,"orders"),{
                passengerId:auth.currentUser?.uid||null,
                passengerName:riderName,
                passengerAvatar:avatarURL||null,
                passengerOccupation:occupation||"",
                passengerExpertise:expertise||[],
                passengerHobbies:hobbies||[],
                passengerGender:gender||"",
                phone:phone||email||"",
                pickup,dest,
                stops:stops.filter(s=>s.address),
                pax,luggage,hasChild,childSeats,hasPet,petType,
                matchMode,
                fare:"協商",
                fareNum:0,
                distKm:routeMetrics?.km||"",
                durMin:routeMetrics?.min||0,
                isScheduled:!!isScheduled,
                scheduledTime,
                status:"waiting",
                createdAt:serverTimestamp(),
                driverId:null,driverName:null,driverPlate:null,driverPhone:null,
                driverLang:null,driverVtype:null,driverLocation:null,
                rating:null,cancelledBy:null,completedAt:null,
              });
              setOrderId(orderRef.id);
              if(isScheduled){
                alert("✅ 預約成功！車主接受後會通知你。");
                setStep("waiting");
              }else{
                setStep(matchMode==="choose"?"choose":"waiting");
              }
            }catch(err){alert("搵Carpool失敗："+err.message);}
          }}/>
        <p style={{fontSize:16,color:SUB,marginTop:10,textAlign:"center",lineHeight:1.5}}>
          🤝 本平台為社區Carpool社交服務。油費分擔由雙方自行協商，與本平台無關。
        </p>
        </>:rideFormTab==="routes"?<>
        <TabBar tabs={[
          {key:"all",label:"全部"},
          {key:"trips",label:"行程"},
          {key:"requests",label:"請求"},
        ]} active={searchResultsTab} onChange={setSearchResultsTab}/>
        {searchResultsTab!=="requests"&&(
        <>
        <Sec title="搜尋固定路線">
          <Card>
            <p style={{color:SUB,fontSize:15,margin:"0 0 10px",lineHeight:1.5}}>
              用平時講嘅地區／街道／地標即可；會同車主刊登嘅上車點、目的地做文字吻合（可試唔同關鍵字）。
            </p>
            <p style={{color:SUB,fontSize:15,margin:"0 0 4px"}}>上車／出發附近</p>
            <AddressInput placeholder="例：萬錦某路口、Finch Station" value={fixedRouteQueryPickup} onChange={setFixedRouteQueryPickup} onSelect={setFixedRouteQueryPickup}/>
            <div onClick={()=>setMapPicker("fixedRoutePu")}
              style={{background:"#EFF6FF",border:`1px solid #BFDBFE`,borderRadius:10,
                padding:"9px 12px",display:"flex",gap:8,alignItems:"center",cursor:"pointer",marginTop:2,marginBottom:10}}>
              <span style={{fontSize:18}}>🗺️</span>
              <span style={{color:"#3B82F6",fontSize:16,fontWeight:500}}>地圖揀上車位置</span>
            </div>
            <div onClick={()=>{
              const pu=fixedRouteQueryPickup;
              const de=fixedRouteQueryDest;
              setFixedRouteQueryPickup(de);
              setFixedRouteQueryDest(pu);
            }}
              style={{background:"#F8FAFC",border:`1px solid ${LIGHT}`,borderRadius:10,
                padding:"8px 12px",display:"flex",gap:8,alignItems:"center",justifyContent:"center",
                cursor:"pointer",marginBottom:10}}>
              <span style={{fontSize:18,color:SUB,fontWeight:700,lineHeight:1}}>⇅</span>
            </div>
            <p style={{color:SUB,fontSize:15,margin:"0 0 4px"}}>目的地</p>
            <AddressInput placeholder="例：市中心、皮爾遜機場" value={fixedRouteQueryDest} onChange={setFixedRouteQueryDest} onSelect={setFixedRouteQueryDest}/>
            <div onClick={()=>setMapPicker("fixedRouteDe")}
              style={{background:"#EFF6FF",border:`1px solid #BFDBFE`,borderRadius:10,
                padding:"9px 12px",display:"flex",gap:8,alignItems:"center",cursor:"pointer",marginTop:2,marginBottom:12}}>
              <span style={{fontSize:18}}>🗺️</span>
              <span style={{color:"#3B82F6",fontSize:16,fontWeight:500}}>地圖揀目的地</span>
            </div>
            <Btn
              label="🔍 搜尋路線"
              color={fixedRouteQueryPickup.trim()&&fixedRouteQueryDest.trim()?TEAL:"#CBD5E1"}
              disabled={!fixedRouteQueryPickup.trim()||!fixedRouteQueryDest.trim()}
              onClick={()=>setFixedRouteSearchRan(true)}/>
          </Card>
        </Sec>
        {!fixedRouteSearchRan?(
          <Card style={{background:DC,border:`1px solid ${LIGHT}`,marginBottom:10}}>
            <p style={{color:SUB,fontSize:16,margin:0,lineHeight:1.55}}>
              填好上面兩個地址後撳「搜尋路線」。為保障私隱同體驗，唔會一次過顯示所有車主刊登。
            </p>
          </Card>
        ):publicDriverRoutes.length===0?(
          <Empty icon="📭" msg="暫未有公開固定路線"/>
        ):(
          <>
            {fixedRouteSearchResults.length===0?(
              <>
                <Empty icon="🔍" msg="搵唔到吻合路線。可試較短關鍵字、城市名、或換另一種寫法（例如 Markham／萬錦）。"/>
                <Card style={{background:"#FFFBEB",border:`1px solid ${GOLD}33`,marginTop:-4}}>
                  <p style={{color:TEXT,fontSize:15,lineHeight:1.5,margin:"0 0 12px"}}>
                    想要呢條路線？你可以直接送出請求，等車主日後開通。
                  </p>
                  <p style={{color:SUB,fontSize:14,margin:"0 0 4px"}}>你想出發日期</p>
                  <input
                    type="date"
                    min={getMinDate()}
                    max={getMaxDate()}
                    value={fixedRouteRequestDate}
                    onChange={e=>setFixedRouteRequestDate(e.target.value)}
                    style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${LIGHT}`,fontSize:16,color:TEXT,outline:"none",marginBottom:10}}
                  />
                  <p style={{color:SUB,fontSize:14,margin:"0 0 4px"}}>你想出發時間</p>
                  <input
                    type="time"
                    value={fixedRouteRequestTime}
                    onChange={e=>setFixedRouteRequestTime(e.target.value)}
                    style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1.5px solid ${LIGHT}`,fontSize:16,color:TEXT,outline:"none",marginBottom:10}}
                  />
                  <Btn
                    label={
                      fixedRouteRequestBusy
                        ?"提交中…"
                        :(lastRequestedRouteKey===requestedRouteKey?"✅ 已送出此路線請求":"📮 提出此路線請求")
                    }
                    color={fixedRouteRequestBusy||lastRequestedRouteKey===requestedRouteKey?"#94A3B8":TEAL}
                    disabled={fixedRouteRequestBusy||lastRequestedRouteKey===requestedRouteKey||!fixedRouteRequestDate||!fixedRouteRequestTime}
                    onClick={submitRouteRequest}
                  />
                </Card>
              </>
            ):(
              <>
                <p style={{color:SUB,fontSize:14,fontWeight:700,margin:"0 0 8px"}}>最吻合路線</p>
                {fixedRouteSearchResults.map(renderFixedRouteResultCard)}
              </>
            )}
            {otherActiveRouteResults.length>0&&(
              <>
                <p style={{color:SUB,fontSize:14,fontWeight:700,margin:"12px 0 8px"}}>其他可用路線（全部 active）</p>
                {otherActiveRouteResults.map(renderFixedRouteResultCard)}
              </>
            )}
          </>
        )
        }
        <p style={{fontSize:15,color:SUB,marginTop:14,lineHeight:1.5,textAlign:"center"}}>
          取消固定路線預約同樣會計入頻繁取消政策（14 日內每滿 3 次取消＝1 次警告；滿 3 次警告停權 7 日）。
        </p>
        </>
        )}
        {searchResultsTab==="requests"&&(
        <>
        <Card style={{background:"#F8FAFC",border:`1px solid ${LIGHT}`,marginBottom:12}}>
          <p style={{color:SUB,fontSize:15,margin:0,lineHeight:1.55}}>
            呢度會顯示你之前提交過嘅路線請求同處理狀態。若車主開通相符路線，管理員或系統可更新狀態。
          </p>
        </Card>
        {routeRequestsLoading?(
          <Empty icon="⏳" msg="載入路線請求中..."/>
        ):myRouteRequests.length===0?(
          <Empty icon="📭" msg="你仲未提交過路線請求"/>
        ):(
          myRouteRequests.map(req=>{
            const s=routeRequestStatusUi(req.status);
            return(
              <Card key={req.id} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <p style={{color:TEXT,fontSize:16,fontWeight:700,margin:0,flex:1,lineHeight:1.45}}>
                    📍 {req.pickup||"—"} → 🏁 {req.dest||"—"}
                  </p>
                  <Tag label={s.label} color={s.color}/>
                </div>
                <p style={{color:SUB,fontSize:14,margin:"8px 0 0",lineHeight:1.45}}>
                  想出發日期：{req.requestedDate||"—"}
                </p>
                <p style={{color:SUB,fontSize:14,margin:"4px 0 0",lineHeight:1.45}}>
                  想出發時間：{req.requestedTime||"—"}
                </p>
                <p style={{color:SUB,fontSize:13,margin:"4px 0 0",lineHeight:1.45}}>
                  提交時間：{req.createdAt?formatDateTime(req.createdAt):"—"}
                </p>
                {req.status==="open"&&(
                  <Btn label="取消請求" color={RED} style={{marginTop:10}} onClick={()=>cancelRouteRequest(req)}/>
                )}
              </Card>
            );
          })
        )}
        </>
        )}
        </>:<>
        <Card style={{background:"#F8FAFC",border:`1px solid ${LIGHT}`,marginBottom:12}}>
          <p style={{color:SUB,fontSize:15,margin:0,lineHeight:1.55}}>
            呢度會顯示你之前提交過嘅路線請求同處理狀態。若車主開通相符路線，管理員或系統可更新狀態。
          </p>
        </Card>
        {routeRequestsLoading?(
          <Empty icon="⏳" msg="載入路線請求中..."/>
        ):myRouteRequests.length===0?(
          <Empty icon="📭" msg="你仲未提交過路線請求"/>
        ):(
          myRouteRequests.map(req=>{
            const s=routeRequestStatusUi(req.status);
            return(
              <Card key={req.id} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <p style={{color:TEXT,fontSize:16,fontWeight:700,margin:0,flex:1,lineHeight:1.45}}>
                    📍 {req.pickup||"—"} → 🏁 {req.dest||"—"}
                  </p>
                  <Tag label={s.label} color={s.color}/>
                </div>
                <p style={{color:SUB,fontSize:14,margin:"8px 0 0",lineHeight:1.45}}>
                  想出發日期：{req.requestedDate||"—"}
                </p>
                <p style={{color:SUB,fontSize:14,margin:"4px 0 0",lineHeight:1.45}}>
                  想出發時間：{req.requestedTime||"—"}
                </p>
                <p style={{color:SUB,fontSize:13,margin:"4px 0 0",lineHeight:1.45}}>
                  提交時間：{req.createdAt?formatDateTime(req.createdAt):"—"}
                </p>
                {req.status==="open"&&(
                  <Btn label="取消請求" color={RED} style={{marginTop:10}} onClick={()=>cancelRouteRequest(req)}/>
                )}
              </Card>
            );
          })
        )}
        </>}
      </Wrap>
      <BottomNav active={navTab} onChange={handleNavTab} items={bottomNavItems} bottomInset={bottomNavLift}/>
      <PeerProfileModal open={peerModal!=null} role={peerModal?.role} userId={peerModal?.uid} onClose={()=>setPeerModal(null)}/>
    </div>
  );
  }

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: CHOOSE DRIVER ──────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="choose"){
    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
        <Bar title={t("bar.chooseDriver")} onBack={back}/>
        <Wrap>
          <Card style={{background:"#F0FDFA",border:`1px solid ${TEAL}33`}}>
            <p style={{color:TEAL,fontSize:16,margin:0}}>☕ 揀一位你有興趣傾偈嘅車主，享受車內 Coffee Chat！</p>
          </Card>
          {chooseDrivers.length===0?<Empty icon="🕐" msg="搜索中..."/>:
          chooseDrivers.map(d=>(
            <Card key={d.id} style={{cursor:"pointer"}} onClick={async()=>{
              try{
                const avgR=d.ratingCount>0?d.totalRating/d.ratingCount:0;
                const modelLine=[d.vehicleModel,d.vehicleTrim].filter(Boolean).join(" · ")||d.vehicleModel||null;
                await updateDoc(doc(db,"orders",orderId),{
                  status:"awaiting_driver",
                  passengerInvitedAt:serverTimestamp(),
                  driverId:d.id,driverName:d.name,
                  driverPlate:d.plate,driverPhone:d.phone||"",
                  driverLang:d.langs,driverVtype:d.vehicleType,
                  driverAvgRating:avgR,
                  driverOccupation:d.occupation||"",
                  driverExpertise:d.expertise||[],
                  driverHobbies:d.hobbies||[],
                  driverSelfieURL:d.selfieURL||null,
                  driverLitersPer100km:d.litersPer100km??null,
                  driverVehicleYear:d.vehicleYear??null,
                  driverVehicleMake:d.vehicleMake??null,
                  driverVehicleModel:modelLine,
                });
                setMatchedDriver({
                  driverId:d.id,driverName:d.name,driverPlate:d.plate,
                  driverPhone:d.phone||"",driverVtype:d.vehicleType,driverLang:d.langs,
                  driverOccupation:d.occupation||"",driverExpertise:d.expertise||[],driverHobbies:d.hobbies||[],
                  driverLitersPer100km:d.litersPer100km??null,
                  driverVehicleYear:d.vehicleYear??null,
                  driverVehicleMake:d.vehicleMake??null,
                  driverVehicleModel:modelLine,
                });
                setCurrentOrderStatus("awaiting_driver");
                setStep("waiting");
              }catch(e){alert("選擇失敗");}
            }}>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                <button type="button" onClick={e=>{e.stopPropagation();setPeerModal({role:"driver",uid:d.id});}}
                  style={{border:"none",background:"transparent",padding:0,cursor:"pointer",flexShrink:0}} title="睇檔案">
                  <div style={{width:48,height:48,borderRadius:"50%",background:DC,overflow:"hidden",
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,border:`2px solid ${LIGHT}`}}>
                    {d.selfieURL?<img src={d.selfieURL} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>:"👤"}
                  </div>
                </button>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <p style={{color:TEXT,fontWeight:700,fontSize:17,margin:0}}>{d.name}</p>
                    <StarDisplay rating={parseFloat(d.avgRating)} count={d.ratingCount||0} size={12}/>
                  </div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
                    <Tag label={`🚗 ${d.vehicleType}`} color={SUB}/>
                    {d.langs?.map(l=><Tag key={l} label={l} color={SUB}/>)}
                    {d.acceptChild&&<Tag label="👶 接細路" color={GREEN}/>}
                    {d.acceptPet&&<Tag label="🐾 接寵物" color={WARN}/>}
                  </div>
                  {/* Show Coffee Chat interests */}
                  {(d.occupation||d.expertise?.length>0||d.hobbies?.length>0)&&(
                    <div style={{marginTop:6}}>
                      <InterestTags occupation={d.occupation} expertise={d.expertise||[]} hobbies={d.hobbies||[]}/>
                    </div>
                  )}
                  {d.bio&&<p style={{color:SUB,fontSize:15,margin:"4px 0 0"}}>{d.bio}</p>}
                  <button type="button" onClick={e=>{e.stopPropagation();setPeerModal({role:"driver",uid:d.id});}}
                    style={{marginTop:8,background:"transparent",border:"none",color:TEAL,fontSize:16,fontWeight:700,cursor:"pointer",textDecoration:"underline",padding:0}}>
                    👤 睇完整檔案
                  </button>
                </div>
                <span style={{color:TEAL,fontWeight:700}}>選擇 →</span>
              </div>
            </Card>
          ))}
        </Wrap>
        <PeerProfileModal open={peerModal!=null} role={peerModal?.role} userId={peerModal?.uid} onClose={()=>setPeerModal(null)}/>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: WAITING ────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="waiting")return(
    <div className="go-fade" style={{minHeight:"100vh",background:BG,display:"flex",
      flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,paddingBottom:24+bottomNavLift}}>
      {chatUnreadCount>0&&(
        <button type="button" onClick={focusWaitingChat}
          style={{marginBottom:10,background:"#ECFDF5",border:`1px solid ${GREEN}55`,color:GREEN,borderRadius:999,padding:"8px 14px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
          💬 新訊息 {chatUnreadCount}
        </button>
      )}
      {currentOrderStatus==="awaiting_passenger"&&matchedDriver?(
        <>
          <p style={{color:TEXT,fontSize:20,fontWeight:700,marginBottom:8,textAlign:"center"}}>🚗 有車主想接你單</p>
          <p style={{color:SUB,fontSize:16,marginBottom:16,textAlign:"center",lineHeight:1.5}}>請確認是否同意配對（雙方確認後先正式出發）</p>
          <Card style={{width:"100%",maxWidth:360,marginBottom:12}}>
            <button type="button" onClick={()=>matchedDriver.driverId&&setPeerModal({role:"driver",uid:matchedDriver.driverId})}
              style={{width:"100%",textAlign:"left",background:"transparent",border:"none",padding:0,cursor:matchedDriver.driverId?"pointer":"default"}}>
              <p style={{color:TEXT,fontSize:18,fontWeight:700,margin:"0 0 6px"}}>{matchedDriver.driverName}</p>
              <p style={{color:SUB,fontSize:16,margin:0}}>🚙 {matchedDriver.driverPlate} · {matchedDriver.driverVtype}</p>
              {matchedDriver.driverOccupation&&<p style={{color:GOLD,fontSize:15,margin:"8px 0 0"}}>💼 {matchedDriver.driverOccupation}</p>}
              {(matchedDriver.driverExpertise?.length>0||matchedDriver.driverHobbies?.length>0)&&(
                <div style={{marginTop:8}}><InterestTags occupation="" expertise={matchedDriver.driverExpertise||[]} hobbies={matchedDriver.driverHobbies||[]}/></div>
              )}
              {matchedDriver.driverId&&<p style={{color:TEAL,fontSize:15,margin:"10px 0 0",fontWeight:700}}>👤 撳此睇完整車主檔案 →</p>}
            </button>
          </Card>
          <Card style={{width:"100%",maxWidth:360,marginBottom:12}}>
            <p style={{color:SUB,fontSize:16,margin:"0 0 4px"}}>👤 {riderName}</p>
            <p style={{color:TEXT,fontSize:16,margin:"0 0 3px"}}>📍 {pickup}</p>
            {stops.filter(s=>s.address).map((s,i)=>(
              <p key={i} style={{color:TEXT,fontSize:16,margin:"0 0 3px"}}>📌 {s.address}</p>
            ))}
            <p style={{color:TEXT,fontSize:16,margin:0}}>🏁 {dest}</p>
          </Card>
          <div style={{width:"100%",maxWidth:360,display:"flex",flexDirection:"column",gap:10}}>
            <Btn label="✅ 確認配對" color={GREEN} onClick={passengerAcceptDriverOffer}/>
            <Btn label="↩️ 婉拒呢位車主" color={WARN} textColor={WHITE} onClick={passengerRejectDriverOffer}/>
            <Btn label="取消成個Carpool請求" color={RED} onClick={cancelOrder}/>
          </div>
        </>
      ):currentOrderStatus==="awaiting_driver"&&matchedDriver?(
        <>
          <div style={{fontSize:48,marginBottom:12,animation:"go-spin 2s linear infinite",display:"inline-block"}}>⏳</div>
          <p style={{color:TEXT,fontSize:20,fontWeight:700,marginBottom:6,textAlign:"center"}}>已邀請車主</p>
          <p style={{color:SUB,fontSize:17,marginBottom:16,textAlign:"center",lineHeight:1.5}}>
            已發送邀請俾 <b style={{color:TEXT}}>{matchedDriver.driverName}</b>，等候佢確認…
          </p>
          <Card style={{width:"100%",maxWidth:360,marginBottom:12}}>
            <p style={{color:SUB,fontSize:16,margin:"0 0 4px"}}>📍 {pickup}</p>
            <p style={{color:TEXT,fontSize:16,margin:0}}>🏁 {dest}</p>
          </Card>
          {matchedDriver?.driverId&&(
            <Btn label="👤 睇車主完整檔案" color={TEAL} style={{width:"100%",maxWidth:360,marginBottom:10}}
              onClick={()=>setPeerModal({role:"driver",uid:matchedDriver.driverId})}/>
          )}
          <div style={{width:"100%",maxWidth:360,display:"flex",flexDirection:"column",gap:10}}>
            {matchMode==="choose"&&(
              <Btn label="🔄 改揀另一位車主" color={TEAL} onClick={passengerChangeDriverChoice}/>
            )}
            <Btn label="取消搵Carpool" color={RED} onClick={cancelOrder}/>
          </div>
        </>
      ):(
        <>
          <div style={{fontSize:52,marginBottom:16,animation:"go-spin 2s linear infinite",display:"inline-block"}}>🔄</div>
          <p style={{color:TEXT,fontSize:20,fontWeight:700,marginBottom:6}}>
            {isScheduled?"已預約，等緊車主接單…":"配對緊…"}
          </p>
          <p style={{color:SUB,fontSize:17,marginBottom:20}}>附近車主會見到你嘅請求</p>
          <Card style={{width:"100%",maxWidth:360}}>
            <p style={{color:SUB,fontSize:16,margin:"0 0 4px"}}>👤 {riderName}</p>
            <p style={{color:TEXT,fontSize:16,margin:"0 0 3px"}}>📍 {pickup}</p>
            {stops.filter(s=>s.address).map((s,i)=>(
              <p key={i} style={{color:TEXT,fontSize:16,margin:"0 0 3px"}}>📌 {s.address}</p>
            ))}
            <p style={{color:TEXT,fontSize:16,margin:0}}>🏁 {dest}</p>
          </Card>
          <div style={{width:"100%",maxWidth:360,marginTop:12}}>
            <MapView pickupAddr={pickup}/>
          </div>
          <div style={{width:"100%",maxWidth:360,marginTop:12,display:"flex",flexDirection:"column",gap:10}}>
            {matchMode==="choose"&&(
              <Btn label="👀 重新揀車主" color={TEAL} onClick={()=>setStep("choose")}/>
            )}
            <Btn label="取消搵Carpool" color={RED} onClick={cancelOrder}/>
          </div>
        </>
      )}
      {orderId&&matchedDriver?.driverId&&["awaiting_passenger","awaiting_driver"].includes(currentOrderStatus)&&(
        <div ref={waitingChatRef} onClick={()=>setChatUnreadCount(0)} style={{width:"100%",maxWidth:360,marginTop:12}}>
          <ChatBox
            messages={chatMessages}
            onSend={sendChatMessage}
            myId={auth.currentUser?.uid}
            myName={riderName}
            h={180}
          />
        </div>
      )}
      <PeerProfileModal open={peerModal!=null} role={peerModal?.role} userId={peerModal?.uid} onClose={()=>setPeerModal(null)}/>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // ── RENDER: MATCHED ────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════

  if(step==="matched"){
    if(recDone)return(
      <div className="go-fade" style={{minHeight:"100vh",background:BG,display:"flex",
        flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,paddingBottom:24+bottomNavLift}}>
        <div style={{fontSize:64,marginBottom:16}}>🎉</div>
        <p style={{color:TEXT,fontSize:22,fontWeight:700,marginBottom:8}}>多謝評價！</p>
        <Btn label="返回主頁" color={TEAL} style={{maxWidth:200}}
          onClick={()=>{setRecDone(false);setRecSel(null);setRating(0);setOrderId(null);setMatchedDriver(null);setDriverLoc(null);setChatMessages([]);onBack();}}/>
      </div>
    );

    const driverPhone=(matchedDriver?.driverPhone||"").replace(/\D/g,"");
    const driverPhoneFull=driverPhone.startsWith("1")?driverPhone:"1"+driverPhone;

    const isUpcoming=currentOrderStatus==="scheduled_pending";

    return(
      <div style={{minHeight:"100vh",background:BG,paddingBottom:bottomNavLift}}>
        <Bar title={isUpcoming?t("bar.tripUpcoming"):t("bar.tripActive")} onBack={back}
          right={!isUpcoming?(
            <button onClick={triggerSOS}
              style={{background:RED,color:WHITE,border:"none",borderRadius:10,
                padding:"6px 14px",fontSize:16,fontWeight:700,cursor:"pointer"}}>🆘 SOS</button>
          ):null}/>
        <Wrap>
          {isUpcoming?(
            <div style={{background:"#EEF2FF",border:"1px solid #818CF844",borderRadius:12,
              padding:"12px 16px",marginBottom:14}}>
              <p style={{color:"#3730A3",fontWeight:700,margin:0}}>📅 預約已確認 · 待出發</p>
              <p style={{color:SUB,fontSize:16,margin:"8px 0 0",lineHeight:1.45}}>
                出發時間：<b style={{color:TEXT}}>{matchedDriver?.scheduledTime?formatDateTime(matchedDriver.scheduledTime):"—"}</b>
              </p>
              <p style={{color:SUB,fontSize:15,margin:"8px 0 0",lineHeight:1.45}}>
                到咗預約時間會自動進入「進行中」模式。你而家可以返回主頁，隨時喺上面「預約」卡片或「紀錄」再入嚟睇。
              </p>
              <Btn label="返回搵Carpool主頁" color={TEAL} style={{marginTop:12}}
                onClick={()=>{setNavTab("ride");setStep("order");}}/>
            </div>
          ):(
            <div style={{background:"#ECFDF5",border:`1px solid ${GREEN}33`,borderRadius:12,
              padding:"12px 16px",marginBottom:14,display:"flex",gap:10,alignItems:"center"}}>
              <span style={{fontSize:22}}>✅</span>
              <div>
                <p style={{color:GREEN,fontWeight:700,margin:0}}>配對成功！{matchedDriver?.driverName||"車主"}已接受Carpool</p>
                <p style={{color:"#6EE7B7",fontSize:15,margin:0}}>
                  {etaData?`預計 ${etaData.durText} 後到達`:"車主正在前往中"}
                </p>
              </div>
            </div>
          )}

          <MapView pickupAddr={pickup} driverLoc={isUpcoming?null:driverLoc} h={250}/>

          <Card style={{marginTop:14}}>
            <button type="button" disabled={!matchedDriver?.driverId}
              onClick={()=>matchedDriver?.driverId&&setPeerModal({role:"driver",uid:matchedDriver.driverId})}
              style={{width:"100%",textAlign:"left",background:"transparent",border:"none",padding:0,cursor:matchedDriver?.driverId?"pointer":"default",marginBottom:12}}>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                <div style={{width:52,height:52,borderRadius:"50%",background:DC,overflow:"hidden",
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>
                  {matchedDriver?.driverSelfieURL?<img src={matchedDriver.driverSelfieURL} style={{width:"100%",height:"100%",objectFit:"cover"}} alt=""/>:"👨"}
                </div>
                <div style={{flex:1}}>
                  <p style={{color:TEXT,fontWeight:700,fontSize:18,margin:"0 0 5px"}}>{matchedDriver?.driverName||"車主"}</p>
                  {matchedDriver?.driverAvgRating>0&&<StarDisplay rating={matchedDriver.driverAvgRating}/>}
                  {matchedDriver?.driverId&&<p style={{color:TEAL,fontSize:14,margin:"6px 0 0",fontWeight:600}}>撳此睇完整檔案</p>}
                </div>
              </div>
            </button>
            {/* Show driver's Coffee Chat interests */}
            {(matchedDriver?.driverOccupation||matchedDriver?.driverExpertise?.length>0||matchedDriver?.driverHobbies?.length>0)&&(
              <div style={{marginBottom:12}}>
                <p style={{color:GOLD,fontSize:15,fontWeight:600,marginBottom:4}}>☕ Coffee Chat 話題</p>
                <InterestTags occupation={matchedDriver.driverOccupation} expertise={matchedDriver.driverExpertise||[]} hobbies={matchedDriver.driverHobbies||[]}/>
              </div>
            )}
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
              {(matchedDriver?.driverLang||[]).map(l=>(
                <span key={l} style={{background:DC,border:`1px solid ${LIGHT}`,borderRadius:20,padding:"3px 9px",fontSize:14,color:SUB}}>{l}</span>
              ))}
            </div>
            <div style={{background:DC,borderRadius:10,padding:12,marginBottom:12}}>
              {(isUpcoming
                ?[["🚗 車牌",matchedDriver?.driverPlate||"未知"],["🚙 車型",matchedDriver?.driverVtype||"未知"],
                  ["📅 預約",matchedDriver?.scheduledTime?formatDateTime(matchedDriver.scheduledTime):"—"]]
                :[["🚗 車牌",matchedDriver?.driverPlate||"未知"],["🚙 車型",matchedDriver?.driverVtype||"未知"],
                  ["📏 距離",etaData?`${etaData.distText}`:"計算中"],["⏱️ 預計",etaData?etaData.durText:"計算中"]]
              ).map(([k,v])=>(
                <div key={k} style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                  <span style={{color:SUB,fontSize:16}}>{k}</span>
                  <span style={{color:TEXT,fontSize:16,fontWeight:600}}>{v}</span>
                </div>
              ))}
              {(matchedDriver?.driverVehicleYear||matchedDriver?.driverVehicleMake||matchedDriver?.driverVehicleModel)&&(
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                  <span style={{color:SUB,fontSize:16}}>🚗 登記車輛</span>
                  <span style={{color:TEXT,fontSize:16,fontWeight:600,textAlign:"right",maxWidth:"65%",lineHeight:1.35}}>
                    {[matchedDriver.driverVehicleYear,matchedDriver.driverVehicleMake,matchedDriver.driverVehicleModel].filter(Boolean).join(" ")}
                  </span>
                </div>
              )}
              {(matchedDriver?.distKm!=null&&String(matchedDriver.distKm).trim()!=="")&&(
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                  <span style={{color:SUB,fontSize:16}}>📏 訂單路程</span>
                  <span style={{color:TEXT,fontSize:16,fontWeight:600}}>{matchedDriver.distKm} km</span>
                </div>
              )}
              <div style={{marginTop:6,paddingTop:8,borderTop:`1px solid ${LIGHT}`}}>
                <span style={{color:SUB,fontSize:15,display:"block",marginBottom:4}}>⛽ 汽油費估算（即時參考油價）</span>
                <span style={{color:TEXT,fontSize:16,fontWeight:600}}>
                  {(()=>{
                    const km=parseFloat(matchedDriver?.distKm)||matchedDriver?.distKmNum||0;
                    const l100=matchedDriver?.driverLitersPer100km;
                    if(matchedDriver?.driverVtype==="電動車")return "—（電動車）";
                    if(l100==null||l100==="")return "—（車主未儲存油耗）";
                    if(!km)return "—（缺訂單路程公里）";
                    if(!liveGasCadPerL)return "—（暫時讀唔到油價）";
                    const cad=estimateTripFuelCad(km,Number(l100),liveGasCadPerL);
                    return cad!=null?`約 $${cad.toFixed(2)} CAD（~${l100} L/100km）`:"—";
                  })()}
                </span>
                <p style={{color:SUB,fontSize:13,margin:"6px 0 0",lineHeight:1.45}}>非叫車報價；實際分擔請同車主協商。油價來源：OpenVan 公開參考數據。</p>
              </div>
            </div>

            <div style={{display:"flex",gap:8,marginBottom:8}}>
              <a href={`tel:+${driverPhoneFull}`} style={{flex:1,background:DC,border:`1px solid ${LIGHT}`,
                borderRadius:12,padding:"12px 0",fontSize:16,color:TEXT,textAlign:"center",
                textDecoration:"none",display:"block",fontWeight:600}}>📞 致電</a>
              <button onClick={()=>{
                const msg=encodeURIComponent(`你好，我係${riderName}，喺${pickup}等你 😊`);
                window.open(`https://wa.me/${driverPhoneFull}?text=${msg}`,"_blank");
              }} style={{flex:1,background:DC,border:`1px solid ${LIGHT}`,borderRadius:12,
                padding:"12px 0",fontSize:16,color:TEXT,fontWeight:600,cursor:"pointer"}}>💬 WhatsApp</button>
            </div>

            {!isUpcoming&&(
              <button onClick={shareTrip}
                style={{width:"100%",background:DC,border:`1px solid ${LIGHT}`,borderRadius:12,
                  padding:"12px 0",fontSize:16,color:TEXT,fontWeight:600,cursor:"pointer",marginBottom:10}}>
                📤 分享行程去WhatsApp
              </button>
            )}

            <p style={{color:SUB,fontSize:15,textAlign:"center",marginBottom:12}}>🤝 油費分擔請私下同車主友好協商</p>
            {isUpcoming&&(
              <Btn label="取消呢個預約" color={RED} style={{marginBottom:12}} onClick={cancelOrder}/>
            )}
          </Card>

          <ChatBox
            messages={chatMessages}
            onSend={sendChatMessage}
            myId={auth.currentUser?.uid}
            myName={riderName}
            h={200}
          />

          {!isUpcoming&&(
            <Card style={{marginTop:14}}>
              <p style={{color:SUB,fontSize:15,marginBottom:8}}>行程完成後：</p>
              <div style={{textAlign:"center",marginBottom:12}}>
                <p style={{color:TEXT,fontSize:16,fontWeight:600,marginBottom:6}}>為車主評分</p>
                <StarRating rating={rating} onRate={setRating} size={32}/>
              </div>
              <div style={{display:"flex",gap:10,marginBottom:10}}>
                {[{v:"recommend",i:"👍",l:"推薦朋友",c:GREEN},{v:"report",i:"🚩",l:"舉報問題",c:RED}].map(o=>(
                  <div key={o.v} onClick={()=>setRecSel(o.v)} style={{flex:1,
                    background:recSel===o.v?o.c+"15":DC,border:`2px solid ${recSel===o.v?o.c:LIGHT}`,
                    borderRadius:12,padding:"14px 0",textAlign:"center",cursor:"pointer"}}>
                    <div style={{fontSize:28}}>{o.i}</div>
                    <div style={{color:recSel===o.v?o.c:SUB,fontSize:15,fontWeight:600,marginTop:4}}>{o.l}</div>
                  </div>
                ))}
              </div>
              <Btn label={rating>0?"⭐ 提交評價":"請先評分"}
                color={rating>0?GREEN:"#CBD5E1"} disabled={!rating}
                onClick={submitRating}/>
            </Card>
          )}
        </Wrap>
        <PeerProfileModal open={peerModal!=null} role={peerModal?.role} userId={peerModal?.uid} onClose={()=>setPeerModal(null)}/>
      </div>
    );
  }

  return null;
};

export default Passenger;
